/* AG Product Builder client runtime.
 *
 * Extracted 2026-08-18 from snippets/agpb-script.liquid, which now emits only a
 * per-section JSON config island plus a deferred tag for this file. The body of
 * initSection() is the old inline IIFE VERBATIM except for exactly nine
 * mechanical substitutions: the root lookup and agpb--js class (moved to the
 * wrapper), the CSS.escape polyfill (moved to this file's top level), six
 * config reads (moneyFormat, currency, noDiscountLine, sfToken, sfShop,
 * financeMonths) and the redirect-to-cart branch (cfg.redirectToCart).
 * Everything else, including every incident-history comment, travels intact.
 *
 * Why an asset: the inline render shipped ~145 KB of uncacheable JS with every
 * PDP view (no browser cache, no V8 code cache) and rode along on every
 * reconcile section fetch. As an asset it downloads once per deploy.
 *
 * Theme editor: a section reload re-inserts the section HTML; the
 * shopify:section:load listener below re-inits the fresh DOM, matching the old
 * inline behaviour (which re-executed automatically). The data-agpb-booted
 * guard keeps a root from ever double-initialising.
 */
(function () {
  'use strict';

  /* CSS.escape polyfill (was inside the per-section IIFE) */
  if (!window.CSS) window.CSS = {};
  if (typeof window.CSS.escape !== 'function') {
    window.CSS.escape = v => String(v || '').replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  }

  function initSection(root, cfg) {
    if (!root || root.hasAttribute('data-agpb-booted')) return;
    root.setAttribute('data-agpb-booted', '');
    root.classList.add('agpb--js');

    // ── Money formatting ────────────────────────────────────────────
    // ── Shared fetch guards ─────────────────────────────────────────
    // Every builder fetch gets a bounded lifetime: a stalled connection must
    // degrade (Liquid fallback price, error message, timer fallback) rather
    // than wedge its UI forever. Same bug class as the spin-wheel incident.
    const _timeoutSig = (ms) => {
      try { return (typeof AbortSignal !== 'undefined' && AbortSignal.timeout) ? AbortSignal.timeout(ms) : undefined; }
      catch (e) { return undefined; }
    };
    // Cart WRITES go through the site-wide AGCart serialiser (ag-cart-queue)
    // so a builder add can never race the free-gift sync or the validator.
    // Queue ENTRY POINTS only, a nested queued fetch deadlocks the chain.
    const _agQueue = (fn) => (window.AGCart && window.AGCart.run) ? window.AGCart.run(fn) : fn();
    // One purchase at a time. ATC and Buy-now disable only THEMSELVES, so a fast
    // double-click across the two buttons fired two concurrent adds and the customer
    // checked out a DOUBLED bundle at doubled price. _agQueue serialises cart writes,
    // it does not DEDUPLICATE them, so the queue alone does not prevent this.
    // Ported from the EU builder (ag-product-builder.js), which has had it since 2026-08-20.
    let _purchaseInFlight = false;

    const moneyFormat  = cfg.moneyFormat;
    const currencyCode = cfg.currency;

    // Shown in a custom-status item's inline notice when it is flagged
    // no-discount, so the customer learns codes are refused here rather than
    // at checkout.
    const AGPB_NO_DISCOUNT_LINE = cfg.noDiscountLine;

    function formatMoney(cents) {
      const n = Number(cents || 0);
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        try { return window.Shopify.formatMoney(n, moneyFormat); } catch(e) {}
      }
      try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: currencyCode }).format(n / 100); }
      catch(e) { return (n / 100).toFixed(2); }
    }

    function renderStaticMoney() {
      root.querySelectorAll('[data-money-cents]').forEach(el => {
        el.textContent = formatMoney(Number(el.getAttribute('data-money-cents') || '0'));
      });
      // Initialise addon tile prefixes to "+". JS will update to "-" if effective cost is negative
      root.querySelectorAll('[data-agpb-addon-item] .agpb__prefix').forEach(el => {
        if (el.textContent === '') el.textContent = '+';
      });
    }

    // ── Gallery ─────────────────────────────────────────────────────
    const mediaJsonEl = root.querySelector('[data-agpb-media-json]');
    const mediaMain   = root.querySelector('[data-agpb-media-main]');
    const mediaStage  = root.querySelector('[data-agpb-media-stage]');
    const thumbEls    = Array.from(root.querySelectorAll('[data-agpb-thumb]'));
    const countEl     = root.querySelector('[data-agpb-media-count]');
    const prevBtn     = root.querySelector('[data-agpb-nav="prev"]');
    const nextBtn     = root.querySelector('[data-agpb-nav="next"]');

    let media       = [];
    let activeIndex = 0;

    // Photo 1 is the collection photo, so the page opens on photo 2
    // (AG-Product-Builder.liquid sends photo 1's URL as skip_preview). Where an
    // option would put photo 1 on the stage, show the opening photo instead.
    // The bundle collage still uses the option's own photo.
    let skipPreview  = '';
    let openPreview  = '';
    // The photo the stage shows for the current base pick. On page load the
    // page opens on photo 2 (openPreview) even when the first option has its
    // own photo, e.g. after photos are reordered in Shopify; a shopper's own
    // pick shows that option's photo.
    let baseStage    = '';
    function showBasePhoto(d, onLoad) {
      baseStage = (onLoad && openPreview) ? openPreview : stagePhotoFor(d.followImage);
      if (baseStage) renderMediaUrl(baseStage, d.title);
    }
    function stagePhotoFor(url) {
      if (!skipPreview || !openPreview || !url) return url;
      const file = (u) => { try { return new URL(u, location.href).pathname; } catch (e) { return ''; } };
      return file(url) === file(skipPreview) ? openPreview : url;
    }

    function setActiveThumb(id) {
      root.querySelectorAll('[data-agpb-thumb]').forEach(b => b.classList.toggle('is-active', b.getAttribute('data-media-id') === String(id)));
    }

    function updateCount() {
      if (!countEl) return;
      countEl.textContent = media.length ? `${activeIndex + 1} / ${media.length}` : '';
    }

    // PERF 2026-09-28: true when that exact image file is already on the
    // stage (no clip over it). At boot the edition pick and the composite
    // restore both asked for the photo the server had just rendered, and
    // rebuilding it made the page's largest image a script-built copy with
    // no srcset or priority: LCP waited on this file (Lighthouse mobile
    // 4 s to 28 s run to run). Callers keep the shown image instead.
    function stageShows(url) {
      if (!mediaStage || !url || mediaStage.querySelector('.agpb__clipWrap')) return false;
      const shown = mediaStage.querySelector('.agpb__mainImg:not(.agpb__mainImg--leaving)');
      if (!shown) return false;
      const file = (u) => { try { return new URL(u, location.href).pathname; } catch (e) { return ''; } };
      return file(shown.currentSrc || shown.src) === file(url);
    }

    function swapToUrl(url, altText) {
      if (!mediaStage || !url) return;
      if (stageShows(url)) { mediaStage.dataset.agpbWant = url; return; }
      // Claim the stage: only the latest swap's onload may apply. A stale
      // image load must never tear down whatever the user chose after it
      // (it used to be able to remove a playing in-action clip).
      mediaStage.dataset.agpbWant = url;
      const img      = new Image();
      img.decoding   = 'async';
      img.loading    = 'eager';
      // CLS FIX 2026-08-17: intrinsic dimensions matter here, not just on the
      // server-rendered <img>. '--entering' is position:absolute (out of flow,
      // so 0 height); dropping that class puts the image back in flow, and
      // without width/height it goes 0 -> 638px and shoves the page down. That
      // measured 0.065 of a 0.12 CLS at 1920x1080. Square matches the 1/1 box
      // on .agpb__mediaMain; CSS object-fit:contain still governs what is drawn,
      // so a non-square source is unaffected visually.
      img.width      = 1400;
      img.height     = 1400;
      img.className  = 'agpb__mainImg agpb__mainImg--entering';
      img.alt        = altText || '';
      img.src        = url;
      img.onload     = () => {
        if (mediaStage.dataset.agpbWant !== url) return;
        const old = mediaStage.querySelector('.agpb__mainImg, .agpb__clipWrap');
        if (old) old.classList.add('agpb__mainImg--leaving');
        mediaStage.appendChild(img);
        requestAnimationFrame(() => img.classList.remove('agpb__mainImg--entering'));
        setTimeout(() => { if (old) old.remove(); }, 200);
      };
    }

    // Render a 3D model slide (2 Nov 2026): the model spins slowly and takes no input, so
    // swipes and arrows still move through the gallery; a tap opens the full-screen viewer
    // (window.AgModel3D in product-model.js, which the section loads when the product has a
    // model). The poster shows until the model has loaded.
    function swapToModel(item) {
      if (!mediaStage) return;
      const wantToken = 'model:' + (item.id || '');
      mediaStage.dataset.agpbWant = wantToken;
      const old  = mediaStage.querySelector('.agpb__mainImg, .agpb__clipWrap');
      const wrap = document.createElement('div');
      wrap.className = 'agpb__clipWrap agpb__modelWrap';
      if (item.preview) {
        const p = new Image();
        p.className = 'agpb__clipPoster';
        p.decoding  = 'async';
        p.alt       = item.alt || '';
        p.src       = item.preview;
        wrap.appendChild(p);
      }
      mediaStage.appendChild(wrap);
      if (old) old.remove();
      const model = { src: item.src, poster: item.preview, alt: item.alt };
      withModel3d(() => {
        window.AgModel3D.ready(() => {
          if (!wrap.isConnected || mediaStage.dataset.agpbWant !== wantToken) return;
          const viewer = document.createElement('model-viewer');
          viewer.setAttribute('src', item.src);
          if (item.preview) viewer.setAttribute('poster', item.preview);
          viewer.setAttribute('alt', item.alt || '');
          window.AgModel3D.makeSpinner(viewer);
          viewer.addEventListener('load', () => wrap.classList.add('is-playing'), { once: true });
          wrap.appendChild(viewer);
          wrap.appendChild(window.AgModel3D.makeOpener(model, item.alt));
        });
      });
    }
    // product-model.js loads deferred; wait for it if a model slide is shown first
    function withModel3d(cb) {
      if (window.AgModel3D) { cb(); return; }
      window.addEventListener('load', () => { if (window.AgModel3D) cb(); }, { once: true });
    }
    function openModelIfActive() {
      const item = media[activeIndex];
      if (!item || item.kind !== 'model' || !item.src) return false;
      withModel3d(() => window.AgModel3D.open({ src: item.src, poster: item.preview, alt: item.alt }));
      return true;
    }

    // Render an in-action clip into the main stage. The <video> is built only
    // when a clip is selected, so nothing preloads on page load (the fix that
    // shipped for the standalone See-It-In-Action row).
    function swapToClip(item) {
      if (!mediaStage) return;
      const wantToken = 'clip:' + (item.id || '');
      mediaStage.dataset.agpbWant = wantToken;
      const old  = mediaStage.querySelector('.agpb__mainImg, .agpb__clipWrap');
      const wrap = document.createElement('div');
      wrap.className = 'agpb__clipWrap';
      if (item.poster) {
        const p = new Image();
        p.className = 'agpb__clipPoster';
        p.decoding  = 'async';
        p.alt       = item.caption || '';
        p.src       = item.poster;
        wrap.appendChild(p);
      }
      // MP4 first: desktop Chrome selects the HLS (m3u8) source if listed
      // first but cannot advance it (readyState 4, currentTime stuck at 0).
      // Every target browser plays the MP4; HLS stays as a fallback.
      const sources = (Array.isArray(item.sources) ? item.sources.slice() : [])
        .sort((a, b) => {
          const am = /mp4/i.test(a.type || '') ? 0 : 1;
          const bm = /mp4/i.test(b.type || '') ? 0 : 1;
          return am - bm;
        });
      let vid = null;
      if (sources.length) {
        vid = document.createElement('video');
        vid.className = 'agpb__clipVideo';
        vid.muted = true; vid.loop = true; vid.playsInline = true;
        vid.setAttribute('playsinline', ''); vid.setAttribute('muted', '');
        vid.preload = 'none';
        sources.forEach(s => {
          const el = document.createElement('source');
          el.src = s.url; if (s.type) el.type = s.type;
          vid.appendChild(el);
        });
        wrap.appendChild(vid);
      }
      const badge = document.createElement('span');
      badge.className = 'agpb__clipBadge';
      badge.innerHTML = '<span class="agpb__clipPlay" aria-hidden="true"></span>' + (item.duration ? item.duration : 'See It In Action');
      wrap.appendChild(badge);
      if (item.caption) {
        const cap = document.createElement('div');
        cap.className = 'agpb__clipCap';
        cap.textContent = item.caption;
        wrap.appendChild(cap);
      }
      mediaStage.appendChild(wrap);
      if (old) old.remove();
      if (vid) {
        try { vid.load(); } catch (e) {}
        const pr = vid.play();
        if (pr && pr.catch) pr.catch(() => {});
        vid.addEventListener('playing', () => wrap.classList.add('is-playing'), { once: true });
        // Keep the clip running while it is the active stage content. The
        // theme's pauseAllMedia() pauses every <video> on modal open/close,
        // and some pauses are silent (no 'pause' event fires, e.g. engine-
        // internal pause steps), so an event listener alone cannot recover.
        // A watchdog restarts a stalled clip and dies with the clip.
        let lastT = -1;
        const watchdog = setInterval(() => {
          if (!wrap.isConnected || mediaStage.dataset.agpbWant !== wantToken) {
            clearInterval(watchdog);
            return;
          }
          if (document.visibilityState !== 'visible') return;
          const stalled = vid.paused || (vid.readyState >= 2 && vid.currentTime === lastT && !vid.seeking);
          lastT = vid.currentTime;
          if (stalled) {
            const rp = vid.play();
            if (rp && rp.catch) rp.catch(() => {});
          }
        }, 600);
      }
    }

    // Media-filter chips (All / Photos / In Action).
    let filterKind = 'all';
    function mediaPasses(item) {
      if (!item) return false;
      const k = item.kind === 'model' ? 'photo' : (item.kind || 'photo');
      return filterKind === 'all' || k === filterKind;
    }
    function stepMedia(dir) {
      if (!media.length) return;
      let i = activeIndex;
      for (let n = 0; n < media.length; n++) {
        i = (i + dir + media.length) % media.length;
        if (mediaPasses(media[i])) { goToIndex(i); return; }
      }
    }

    // Read clips published by the AG-See-In-Action section and weave them into
    // the gallery (one image, one clip, one image, ...) with filter chips.
    function enhanceWithSiaClips() {
      const blob = document.querySelector('[data-ag-sia-clips]');
      if (!blob) return;
      let clips = [];
      try { clips = JSON.parse(blob.textContent || '[]'); } catch (e) { return; }
      clips = (Array.isArray(clips) ? clips : []).filter(c => c && Array.isArray(c.sources) && c.sources.length);
      if (!clips.length) return;

      // Ensure a thumb strip exists (a single-image product would not have one).
      let thumbRow = root.querySelector('[data-agpb-thumbs]');
      if (!thumbRow) {
        thumbRow = document.createElement('div');
        thumbRow.className = 'agpb__thumbRow';
        thumbRow.setAttribute('data-agpb-thumbs', '');
        if (mediaMain && mediaMain.parentNode) mediaMain.parentNode.insertBefore(thumbRow, mediaMain.nextSibling);
        else return;
      }

      // Existing image media + thumbs, captured before weaving in the clips.
      const photoMedia  = media.slice();
      const photoCount  = photoMedia.length;
      const photoThumbs = Array.from(thumbRow.querySelectorAll('[data-agpb-thumb]'));
      photoThumbs.forEach(t => { if (!t.getAttribute('data-kind')) t.setAttribute('data-kind', 'photo'); });

      // Build clip media + clip thumbnails.
      const clipMedia = [];
      const clipThumbs = [];
      clips.forEach((c, i) => {
        clipMedia.push({
          id: c.id, kind: 'clip',
          poster: c.poster || null, thumb: c.thumb || null, caption: c.caption || '',
          duration: c.duration || '', link: c.link || '', sources: c.sources
        });
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'agpb__thumb agpb__thumb--clip';
        btn.setAttribute('data-agpb-thumb', '');
        btn.setAttribute('data-kind', 'clip');
        btn.setAttribute('data-media-id', c.id);
        btn.setAttribute('aria-label', 'Play See It In Action clip ' + (i + 1));
        const inner = document.createElement('div');
        inner.className = 'agpb__thumbInner';
        if (c.poster) {
          const im = new Image();
          // The thumb is ~220 px wide: use the 440 px image, not the 1400 px
          // stage poster (9 clips x 1400 px was ~2.5 MB on a phone).
          im.loading = 'lazy'; im.src = c.thumb || c.poster; im.alt = c.caption || 'See It In Action clip';
          inner.appendChild(im);
        }
        const play = document.createElement('span'); play.className = 'agpb__thumbPlay'; play.setAttribute('aria-hidden', 'true'); inner.appendChild(play);
        if (c.duration) { const d = document.createElement('span'); d.className = 'agpb__thumbDur'; d.textContent = c.duration; inner.appendChild(d); }
        btn.appendChild(inner);
        btn.addEventListener('click', () => {
          const idx = media.findIndex(m => String(m.id) === String(c.id));
          if (idx > -1) goToIndex(idx);
        });
        clipThumbs.push(btn);
      });

      // Weave 1:1, image, clip, image, clip, ... then any remainder.
      function weave(a, b) {
        const out = []; let i = 0, j = 0;
        while (i < a.length || j < b.length) {
          if (i < a.length) out.push(a[i++]);
          if (j < b.length) out.push(b[j++]);
        }
        return out;
      }
      media.splice(0, media.length, ...weave(photoMedia, clipMedia));
      weave(photoThumbs, clipThumbs).forEach(t => thumbRow.appendChild(t));

      // Filter chips: All / Images / See It In Action.
      const bar = document.createElement('div');
      bar.className = 'agpb__mediaFilter';
      bar.setAttribute('data-agpb-mediafilter', '');
      bar.setAttribute('role', 'tablist');
      const defs = [
        { key: 'all',   label: 'All',              n: media.length },
        { key: 'photo', label: 'Images',           n: photoCount },
        { key: 'clip',  label: 'See It In Action', n: clips.length }
      ];
      const chipEls = [];
      defs.forEach(d => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'agpb__mfChip' + (d.key === 'all' ? ' is-active' : '');
        b.setAttribute('data-agpb-filter', d.key);
        b.setAttribute('role', 'tab');
        b.setAttribute('aria-selected', d.key === 'all' ? 'true' : 'false');
        b.innerHTML = d.label + ' <span class="agpb__mfN">' + d.n + '</span>';
        b.addEventListener('click', () => {
          filterKind = d.key;
          chipEls.forEach(c => { const on = c === b; c.classList.toggle('is-active', on); c.setAttribute('aria-selected', on ? 'true' : 'false'); });
          root.querySelectorAll('[data-agpb-thumb]').forEach(t => {
            const k = t.getAttribute('data-kind') || 'photo';
            t.style.display = (filterKind === 'all' || k === filterKind) ? '' : 'none';
          });
          const first = media.findIndex(mediaPasses);
          if (first > -1) goToIndex(first);
        });
        chipEls.push(b);
        bar.appendChild(b);
      });
      if (thumbRow.parentNode) thumbRow.parentNode.insertBefore(bar, thumbRow);

      updateCount();
    }

    function hideCompositeTemporarily() {
      if (!compositeCanvas) return;
      compositeCanvas.classList.remove('is-visible');
      if (mediaStage) { mediaStage.style.transition = 'opacity .2s ease'; mediaStage.style.opacity = '1'; }
      setTimeout(() => {
        compositeCanvas.classList.add('is-hidden');
        if (mediaStage) mediaStage.style.transition = '';
        setViewBundleVisible(true);
      }, 220);
    }

    function goToIndex(idx) {
      if (!media.length) return;
      activeIndex  = (idx + media.length) % media.length;
      const item   = media[activeIndex];
      if (item && item.kind === 'clip') swapToClip(item);
      else if (item && item.kind === 'model' && item.src) swapToModel(item);
      else if (item && item.preview) swapToUrl(item.preview, item.alt || '');
      setActiveThumb(item.id);
      updateCount();
      // Hide composite while browsing; restore when back at index 0
      if (activeIndex === 0) {
        renderCompositeCanvas();
      } else {
        hideCompositeTemporarily();
      }
    }

    if (mediaJsonEl && mediaMain && mediaStage) {
      try {
        const data = JSON.parse(mediaJsonEl.textContent || '{}');
        media      = Array.isArray(data.media) ? data.media : [];
        if (media.length && typeof data.skip_preview === 'string' && data.skip_preview) {
          skipPreview = data.skip_preview;
          openPreview = media[0].preview || '';
        }
        if (media.length) {
          activeIndex = 0;
          // PERF 2026-09-28: the server already rendered media[0] as the main
          // image (fetchpriority high, srcset). Re-creating it here made the
          // page's largest image a script-built copy, so LCP waited on this
          // file (Lighthouse mobile: 4 s to 28 s run to run). Only swap when
          // the server showed something else.
          const serverShowsFirst = data.server_media_id != null &&
            String(data.server_media_id) === String(media[0].id) &&
            mediaStage.querySelector('.agpb__mainImg');
          if (media[0].preview && !serverShowsFirst) swapToUrl(media[0].preview, media[0].alt || '');
          setActiveThumb(media[0].id);
          updateCount();
        }
        thumbEls.forEach(btn => {
          btn.addEventListener('click', () => {
            const id  = btn.getAttribute('data-media-id');
            const idx = media.findIndex(m => String(m.id) === String(id));
            if (idx > -1) goToIndex(idx);
          });
        });
        if (prevBtn) prevBtn.addEventListener('click', () => stepMedia(-1));
        if (nextBtn) nextBtn.addEventListener('click', () => stepMedia(1));

        // Pull the "See It In Action" clips published by the AG-See-In-Action
        // section on the same page, and fold them into this gallery as an
        // "In Action" filter. No clips on the page => photos-only, unchanged.
        // The SIA section renders later in the page than this builder, so its
        // clip blob may not exist yet during parse, wait for the DOM.
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', enhanceWithSiaClips, { once: true });
        } else {
          enhanceWithSiaClips();
        }

        if (media.length > 1) {
          let startX = 0, startY = 0, tracking = false;
          mediaMain.addEventListener('touchstart', e => {
            const t = e.touches && e.touches[0];
            if (!t) return;
            tracking = true; startX = t.clientX; startY = t.clientY;
          }, { passive: true });
          mediaMain.addEventListener('touchend', e => {
            if (!tracking) return;
            tracking = false;
            const t  = e.changedTouches && e.changedTouches[0];
            if (!t) return;
            const dx = t.clientX - startX;
            const dy = t.clientY - startY;
            if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
              dx < 0 ? stepMedia(1) : stepMedia(-1);
            }
          }, { passive: true });
        }
      } catch(e) {}
    }

    function renderMediaUrl(url, altText) {
      swapToUrl(url, altText);
      thumbEls.forEach(b => b.classList.remove('is-active'));
      if (countEl) countEl.textContent = '';
    }

    // ── Lightbox ─────────────────────────────────────────────────────
    const lightbox     = root.querySelector('[data-agpb-lightbox]');
    const lbImg        = lightbox && lightbox.querySelector('[data-agpb-lightbox-img]');
    const lbCount      = lightbox && lightbox.querySelector('[data-agpb-lightbox-count]');
    const lbPrev       = lightbox && lightbox.querySelector('[data-agpb-lightbox-prev]');
    const lbNext       = lightbox && lightbox.querySelector('[data-agpb-lightbox-next]');
    const zoomBtn      = root.querySelector('[data-agpb-zoom]');
    let lbIndex        = 0;

    function openLightbox(idx) {
      if (!lightbox || !media.length) return;
      lbIndex = (idx + media.length) % media.length;
      const item = media[lbIndex];
      if (lbImg) { lbImg.src = item.preview || item.poster || ''; lbImg.alt = item.alt || item.caption || ''; }
      if (lbCount) lbCount.textContent = media.length > 1 ? `${lbIndex + 1} / ${media.length}` : '';
      if (lbPrev) lbPrev.classList.toggle('is-hidden', media.length <= 1);
      if (lbNext) lbNext.classList.toggle('is-hidden', media.length <= 1);
      lightbox.classList.remove('is-hidden');
      document.body.style.overflow = 'hidden';
    }

    function closeLightbox() {
      if (!lightbox) return;
      lightbox.classList.add('is-hidden');
      document.body.style.overflow = '';
    }

    if (zoomBtn) {
      zoomBtn.addEventListener('click', e => { e.stopPropagation(); if (!openModelIfActive()) openLightbox(activeIndex); });
    }
    if (mediaStage) {
      mediaStage.style.cursor = 'zoom-in';
      mediaStage.addEventListener('click', () => {
        if (openModelIfActive()) return;
        if (!lightbox || lightbox.classList.contains('is-hidden')) openLightbox(activeIndex);
      });
    }
    if (lbImg) {
      lbImg.addEventListener('click', e => { e.stopPropagation(); closeLightbox(); });
    }
    if (lbPrev) lbPrev.addEventListener('click', e => { e.stopPropagation(); openLightbox(lbIndex - 1); });
    if (lbNext) lbNext.addEventListener('click', e => { e.stopPropagation(); openLightbox(lbIndex + 1); });
    lightbox && lightbox.querySelectorAll('[data-agpb-lightbox-close]').forEach(el => {
      el.addEventListener('click', e => { e.stopPropagation(); closeLightbox(); });
    });
    document.addEventListener('keydown', e => {
      if (!lightbox || lightbox.classList.contains('is-hidden')) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft')  openLightbox(lbIndex - 1);
      if (e.key === 'ArrowRight') openLightbox(lbIndex + 1);
    });
    // Swipe to navigate in lightbox
    if (lightbox) {
      let lbStartX = 0, lbStartY = 0, lbDist = 0;
      lightbox.addEventListener('touchstart', e => {
        if (lightbox.classList.contains('is-hidden')) return;
        lbStartX = e.touches[0].clientX;
        lbStartY = e.touches[0].clientY;
        lbDist = 0;
      }, { passive: true });
      lightbox.addEventListener('touchmove', e => {
        if (lightbox.classList.contains('is-hidden')) return;
        const dx = Math.abs(e.touches[0].clientX - lbStartX);
        const dy = Math.abs(e.touches[0].clientY - lbStartY);
        lbDist = Math.max(dx, dy);
      }, { passive: true });
      lightbox.addEventListener('touchend', e => {
        if (lightbox.classList.contains('is-hidden')) return;
        const dx = e.changedTouches[0].clientX - lbStartX;
        if (lbDist > 40 && Math.abs(dx) > Math.abs(e.changedTouches[0].clientY - lbStartY)) {
          // Horizontal swipe, navigate
          dx < 0 ? openLightbox(lbIndex + 1) : openLightbox(lbIndex - 1);
        } else if (lbDist < 12) {
          // Clean tap, close
          closeLightbox();
        }
      }, { passive: true });
    }

    // ── Builder core ────────────────────────────────────────────────
    const baseItems   = Array.from(root.querySelectorAll('[data-agpb-base-item]'));
    const addonItems  = Array.from(root.querySelectorAll('[data-agpb-addon-item]'));
    const groupHosts  = Array.from(root.querySelectorAll('[data-agpb-tiles]'));

    root.classList.add('agpb--ready');

    // ── Accordions ──────────────────────────────────────────────────
    root.querySelectorAll('[data-agpb-group]').forEach(group => {
      const isAcc      = group.getAttribute('data-accordion') === 'true';
      const openDef    = group.getAttribute('data-open')      === 'true';
      const body       = group.querySelector('[data-agpb-acc-body]');
      const btn        = group.querySelector('[data-agpb-acc-btn]');
      const head       = group.querySelector('[data-agpb-acc-head]');

      if (!isAcc) {
        if (btn)  btn.style.display  = 'none';
        if (body) body.style.display = '';
        return;
      }

      const setOpen = open => {
        group.classList.toggle('is-open', open);
        if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (body) {
          if (open) {
            body.style.maxHeight = body.scrollHeight + 'px';
            body.addEventListener('transitionend', () => {
              if (group.classList.contains('is-open')) body.style.maxHeight = 'none';
            }, { once: true });
          } else {
            body.style.maxHeight = body.scrollHeight + 'px';
            requestAnimationFrame(() => { body.style.maxHeight = '0'; });
          }
        }
      };

      // Set initial state without animation
      if (body) {
        body.style.transition = 'none';
        body.style.maxHeight  = openDef ? 'none' : '0';
        requestAnimationFrame(() => { body.style.transition = ''; });
      }
      group.classList.toggle('is-open', openDef);
      if (btn) btn.setAttribute('aria-expanded', openDef ? 'true' : 'false');

      if (head) {
        head.style.cursor = 'pointer';
        head.addEventListener('click', e => {
          if (e.target.closest('.agpb__tiles')) return;
          setOpen(!group.classList.contains('is-open'));
        });
      }
    });

    // ── Tile layout (fixed cols vs min-width, desktop grid vs mobile swipe) ─
    function applyTileLayout(host) {
      if (!host) return;
      const mobileMode = host.getAttribute('data-mobile-mode') || 'grid';
      const isMobile   = window.innerWidth <= 749;
      // Base tiles always use their own swipe class set in HTML
      if (host.hasAttribute('data-agpb-base-tiles')) {
        if (host.classList.contains('agpb__tiles--swipe')) return;
        const mode = host.getAttribute('data-columns-mode') || 'fixed';
        host.classList.remove('agpb__grid-fixed', 'agpb__grid-minw');
        host.classList.add(mode === 'minw' ? 'agpb__grid-minw' : 'agpb__grid-fixed');
        return;
      }
      // Addon tiles: swipe on mobile if mobile_mode=swipe, else always grid
      if (mobileMode === 'swipe' && isMobile) {
        host.classList.remove('agpb__tiles--grid', 'agpb__grid-fixed', 'agpb__grid-minw');
        host.classList.add('agpb__tiles--swipe');
        host.style.gridTemplateColumns = '';
      } else {
        host.classList.remove('agpb__tiles--swipe');
        host.classList.add('agpb__tiles--grid');
        const mode  = host.getAttribute('data-columns-mode') || 'fixed';
        const group = host.closest('[data-agpb-group]');
        host.classList.remove('agpb__grid-fixed', 'agpb__grid-minw');
        if (mode === 'minw') {
          host.classList.add('agpb__grid-minw');
          const minw = group ? (group.style.getPropertyValue(isMobile ? '--agpb-minw-m' : '--agpb-minw-d') || (isMobile ? '90' : '120')) : (isMobile ? '90' : '120');
          host.style.gridTemplateColumns = 'repeat(auto-fit, minmax(' + minw.trim().replace('px','') + 'px, 1fr))';
        } else {
          host.classList.add('agpb__grid-fixed');
          const cols = group ? (group.style.getPropertyValue(isMobile ? '--agpb-cols-m' : '--agpb-cols-d') || (isMobile ? '2' : '3')) : (isMobile ? '2' : '3');
          host.style.gridTemplateColumns = 'repeat(' + cols.trim() + ', minmax(0, 1fr))';
        }
      }
    }
    function applyAllLayouts() {
      applyTileLayout(root.querySelector('[data-agpb-base-tiles]'));
      groupHosts.forEach(applyTileLayout);
    }
    window.addEventListener('resize', applyAllLayouts);
    applyAllLayouts();

    // ── Compare-prefix per addon item ───────────────────────────────
    addonItems.forEach(item => {
      const key         = item.getAttribute('data-group-key') || '';
      const group       = root.querySelector(`[data-agpb-group][data-group-key="${CSS.escape(key)}"]`);
      const groupPrefix = group ? (group.getAttribute('data-compare-prefix') || '') : '';
      const itemPrefix  = (item.getAttribute('data-compare-prefix-override') || '').trim();
      const prefix      = (itemPrefix || groupPrefix || '').trim();
      const prefixEl    = item.querySelector('[data-agpb-compare-prefix]');
      if (prefixEl) {
        if (prefix) { prefixEl.textContent = prefix; prefixEl.classList.remove('is-hidden'); }
        else        { prefixEl.textContent = '';      prefixEl.classList.add('is-hidden');    }
      }
    });

    // ── State ───────────────────────────────────────────────────────
    // Hoisted 2026-08-18: renderCompositeCanvas can run during the bootstrap
    // sequence (URL-restored add-ons make the collage viable immediately once
    // boot is deferred to DOMContentLoaded), which sits ABOVE the composite
    // block in source order. Leaving this `let` down there was a TDZ crash.
    let compositeRenderToken = 0;

    const addonSelections      = new Map(); // single-select: key → selObj|null
    const addonMultiSelections = new Map(); // multi-select:  key → selObj[]
    const addonPreorderAccepted = new Set(); // group keys whose preorder modal was accepted
    let qty           = 1;
    let baseVariantId = null;
    let baseTitle     = '';
    let basePrice     = 0;
    let acceptPreorder = false; // set to true when user accepts the preorder modal for base

    // ── Helpers ─────────────────────────────────────────────────────
    function setError(msg, targetEl) {
      const err = root.querySelector('[data-agpb-error]');
      if (!err) return;
      if (!msg) { err.textContent = ''; err.classList.add('is-hidden'); return; }
      err.textContent = msg;
      err.classList.remove('is-hidden');
      err.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      if (targetEl) {
        targetEl.classList.remove('is-required-err');
        void targetEl.offsetWidth; // reflow to restart animation
        targetEl.classList.add('is-required-err');
        targetEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        targetEl.addEventListener('animationend', () => targetEl.classList.remove('is-required-err'), { once: true });
      }
    }

    function setActive(btn, active) {
      if (!btn) return;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    }

    function getWrapData(wrap) {
      return {
        variantId   : Number(wrap.getAttribute('data-variant-id')  || '0'),
        realVariant : Number(wrap.getAttribute('data-real-variant') || '0'), // Phase 4: real product variant (cart-add target; carries builder.saving for the discount engine)
        saving      : Number(wrap.getAttribute('data-saving')       || '0'),
        addonKey    : wrap.getAttribute('data-addon-key')          || '',
        title       : wrap.getAttribute('data-title')              || '',
        price       : Number(wrap.getAttribute('data-price')       || '0'),
        available   : wrap.getAttribute('data-available')          === 'true',
        followImage : wrap.getAttribute('data-follow-image')       || '',
        status      : (wrap.getAttribute('data-status')            || '').trim(),
        customLabel : (wrap.getAttribute('data-custom-label')      || '').trim(),
        customNote  : (wrap.getAttribute('data-custom-note')       || '').trim(),
        customColor : (wrap.getAttribute('data-custom-color')      || '').trim(),
        noDiscount  : wrap.getAttribute('data-no-discount')        === 'true',
        notifyLink  : (wrap.getAttribute('data-notify-link')       || '').trim(),
        productId   : wrap.getAttribute('data-product-id')         || '',
        productUrl  : wrap.getAttribute('data-product-url')        || ''
      };
    }

    // ── Status bar pill + notify builder (shared) ───────────────────
    function buildStatusBarHtml(s, wrap) {
      // Custom acknowledge status (e.g. End of Line): the item is buyable, so
      // no date pill and no Notify me, and no status pill either, since the
      // tile's own ribbon already carries the label and the notice says the
      // rest. This is the only place the customer is told about the status, so
      // a no-discount item also spells out that codes will be refused at
      // checkout, and the block's link (if any) rides at the end of the notice.
      // Returns '' when there is nothing to say; the callers hide the bar.
      if (s === 'custom') {
        const label = ((wrap ? wrap.getAttribute('data-custom-label') : '') || '').trim() || 'End of Line';
        const link  = ((wrap ? wrap.getAttribute('data-custom-link')  : '') || '').trim();
        const cc    = ((wrap ? wrap.getAttribute('data-custom-color')  : '') || '').trim();
        const body  = ((wrap ? wrap.getAttribute('data-custom-body')  : '') || '').trim();
        const noDisc = !!(wrap && wrap.getAttribute('data-no-discount') === 'true');
        const cstyle = cc ? ` style="--agpb-custom-color:${cc}"` : '';

        const lines = [];
        if (body)   lines.push(`<span class="agpb__customNoteLine">${escHtml(body)}</span>`);
        if (noDisc) lines.push(`<span class="agpb__customNoteLine agpb__customNoteLine--nd">${escHtml(AGPB_NO_DISCOUNT_LINE)}</span>`);
        if (link)   lines.push(`<a href="${link.replace(/"/g,'&quot;')}" class="agpb__customNoteLine agpb__customNoteLink">${escHtml(label)} ›</a>`);

        return lines.length
          ? `<p class="agpb__customNote"${cstyle}>${lines.join('')}</p>`
          : '';
      }
      const isPreorder   = s === 'preorder';
      const pillCls      = isPreorder ? 'is-preorder' : 'is-oos';
      const itemTitle    = wrap ? (wrap.getAttribute('data-title')         || '') : '';
      const expectedDate = wrap ? (wrap.getAttribute('data-expected-date') || '').trim().replace(/^optional$/i, '') : '';
      const preorderLink = (wrap ? (wrap.getAttribute('data-preorder-link') || '').trim() : '')
        || 'https://assassingoods.com/pages/pre-orders-information-terms-cancellation';
      const notifyLink   = wrap ? (wrap.getAttribute('data-notify-link')   || '').trim() : '';
      // Only a link the merchant typed into the block setting is a real link.
      // data-notify-link also carries the auto product-URL fallback, which the
      // modal uses for the variant id; it must never turn the button into a link.
      const notifyCustom = !!(wrap && wrap.getAttribute('data-notify-custom') === 'true' && notifyLink);

      const responsiveLabel = (full, short) => `<span class="agpb__statusFull">${escHtml(full)}</span><span class="agpb__statusShort">${escHtml(short)}</span>`;
      const pillLabel   = isPreorder ? responsiveLabel('Pre-order information ›', 'Pre-order info ›') : 'Out of stock';
      const notifyLabel = responsiveLabel(notifyCustom ? 'Notify me when available ›' : 'Notify me when available', notifyCustom ? 'Notify me ›' : 'Notify me');

      let datePillHtml = '';
      if (isPreorder && expectedDate) {
        // Always prefix "Estimated" so the pill reads e.g. "Estimated Mid May".
        // Skip the prefix if the source already starts with "Estimated" so we
        // don't end up with "Estimated Estimated …".
        const pillDate = /^estimated\b/i.test(expectedDate) ? expectedDate : 'Estimated ' + expectedDate;
        const shortDate = expectedDate.replace(/^estimated\s+/i, '').replace(/^ships?\s+/i, '');
        datePillHtml = `<span class="agpb__sysPill agpb__sysPill--date">${responsiveLabel(pillDate, 'Ships ' + shortDate)}</span>`;
      }

      const pillHtml = preorderLink && isPreorder
        ? `<a href="${preorderLink.replace(/"/g,'&quot;')}" class="agpb__sysPill agpb__sysPill--link ${pillCls}">${pillLabel}</a>`
        : `<span class="agpb__sysPill ${pillCls}" aria-hidden="true">${pillLabel}</span>`;

      const notifyHtml = notifyCustom
        ? `<a href="${notifyLink.replace(/"/g,'&quot;')}" target="_blank" rel="noopener" class="agpb__notifyTrigger agpb__notifyTrigger--link">${notifyLabel}</a>`
        : `<button type="button" class="agpb__notifyTrigger" data-agpb-notify-trigger
                   data-notify-title="${itemTitle.replace(/"/g,'&quot;')}"
                   data-notify-status="${s}"
                   data-notify-link="${notifyLink.replace(/"/g,'&quot;')}">${notifyLabel}</button>`;

      return `${datePillHtml}${pillHtml}${notifyHtml}`;
    }

    // ── Base status bar (pill + Notify me) ──────────────────────────
    function renderBaseStatus(wrap) {
      const host = root.querySelector('[data-agpb-base-status]');
      if (!host) return;
      const s = String(wrap ? wrap.getAttribute('data-status') || '' : '').toLowerCase().trim();
      if (s !== 'preorder' && s !== 'oos' && s !== 'custom') {
        host.classList.add('is-hidden');
        host.innerHTML = '';
        return;
      }
      // A custom status with no message, no no-discount flag and no link has
      // nothing to render, keep the bar hidden rather than leave a gap.
      const html = buildStatusBarHtml(s, wrap);
      host.classList.toggle('is-hidden', !html);
      host.innerHTML = html;
    }

    // ── Addon group status bar (pill + Notify me) ───────────────────
    function renderAddonStatus(key, wrap) {
      const group = root.querySelector(`[data-agpb-group][data-group-key="${CSS.escape(key)}"]`);
      if (!group) return;
      const host = group.querySelector('[data-agpb-addon-status]');
      if (!host) return;
      const s = String(wrap ? wrap.getAttribute('data-status') || '' : '').toLowerCase().trim();
      if (s !== 'preorder' && s !== 'oos' && s !== 'custom') {
        host.classList.add('is-hidden');
        host.innerHTML = '';
        return;
      }
      const html = buildStatusBarHtml(s, wrap);
      host.classList.toggle('is-hidden', !html);
      host.innerHTML = html;
    }
    const poModal     = root.querySelector('[data-agpb-preorder-modal]');
    const poAcceptBtn = root.querySelector('[data-agpb-po-accept]');
    const poCancelBtn = root.querySelector('[data-agpb-po-cancel]');
    const poBackdrop  = root.querySelector('[data-agpb-modal-backdrop]');

    let _poOnAccept = null;
    let _poOnCancel = null;

    function openPreorderModal(itemTitle, expectedDate, onAccept, onCancel) {
      if (!poModal) { onAccept && onAccept(); return; }
      const nameEl   = poModal.querySelector('[data-agpb-po-product-name]');
      const dateBox  = poModal.querySelector('[data-agpb-po-date-box]');
      const dateEl   = poModal.querySelector('[data-agpb-po-date]');
      if (nameEl)  nameEl.textContent  = itemTitle;
      if (dateBox) dateBox.classList.toggle('is-hidden', !expectedDate);
      if (dateEl && expectedDate)  dateEl.textContent = expectedDate;
      _poOnAccept = onAccept;
      _poOnCancel = onCancel;
      poModal.classList.remove('is-hidden');
      document.body.style.overflow = 'hidden';
      if (poAcceptBtn) poAcceptBtn.focus();
    }

    function closePreorderModal() {
      if (!poModal) return;
      poModal.classList.add('is-hidden');
      document.body.style.overflow = '';
    }

    if (poAcceptBtn) {
      poAcceptBtn.addEventListener('click', () => {
        closePreorderModal();
        if (_poOnAccept) _poOnAccept();
        _poOnAccept = _poOnCancel = null;
      });
    }
    if (poCancelBtn) {
      poCancelBtn.addEventListener('click', () => {
        closePreorderModal();
        if (_poOnCancel) _poOnCancel();
        _poOnAccept = _poOnCancel = null;
      });
    }
    if (poBackdrop) {
      poBackdrop.addEventListener('click', () => {
        closePreorderModal();
        if (_poOnCancel) _poOnCancel();
        _poOnAccept = _poOnCancel = null;
      });
    }

    // Opens the acknowledge modal for a gated tile. Pre-order is the only
    // gated status: a custom status (e.g. End of Line) is surfaced by the
    // inline notice in the status bar instead and never blocks selection.
    function openStatusGate(d, wrap, onAccept, onCancel) {
      const expectedDate = (wrap.getAttribute('data-expected-date') || '').trim().replace(/^optional$/i, '');
      openPreorderModal(d.title, expectedDate, onAccept, onCancel);
    }

    // ── Notify me modal ─────────────────────────────────────────────
    const nmModal    = root.querySelector('[data-agpb-notify-modal]');
    const nmBackdrop = root.querySelector('[data-agpb-notify-backdrop]');
    // nmCancel: handled via delegation on nmModal (there are two Cancel buttons)

    function openNotifyModal(itemTitle, statusLabel, notifyLink, isBase, variantId, productId, productUrl) {
      if (!nmModal) return;
      const provider = nmModal.getAttribute('data-bis-provider') || 'da';
      const nameEl   = nmModal.querySelector('[data-agpb-nm-product-name]');
      if (nameEl) nameEl.textContent = itemTitle;

      if (provider === 'klaviyo') {
        // ── Klaviyo BIS: same inline form for base AND addon OOS ──────
        const bisEl = nmModal.querySelector('[data-klaviyo-bis]');
        if (bisEl) {
          // Variant id: explicit arg wins; else parse ?variant= from the
          // notify link (addon call sites pass only the link).
          let vid = variantId || '';
          if (!vid && notifyLink) {
            const m = notifyLink.match(/[?&]variant=(\d+)/);
            if (m) vid = m[1];
          }
          bisEl.setAttribute('data-variant-id', vid);
          const form    = bisEl.querySelector('[data-klaviyo-bis-form]');
          const okEl    = bisEl.querySelector('[data-klaviyo-bis-success]');
          const errEl   = bisEl.querySelector('[data-klaviyo-bis-error]');
          const mktEl   = bisEl.querySelector('[data-klaviyo-bis-marketing]');
          if (form)  form.hidden = false;
          if (okEl)  okEl.hidden = true;
          if (errEl) errEl.hidden = true;
          if (mktEl) mktEl.checked = false;
          // The modal's OUT OF STOCK banner + product name already title this;
          // the form shows no duplicate heading in the builder.
        }
      } else {
        const restockWrap = nmModal.querySelector('[data-agpb-nm-restock-wrap]');
        const linkWrap    = nmModal.querySelector('[data-agpb-nm-link-wrap]');
        if (isBase && restockWrap) {
          // ── Base OOS: show DA Restock inline form ──────────────────
          restockWrap.style.display = '';
          if (linkWrap) linkWrap.style.display = 'none';
          // Populate hidden inputs with this variant's data
          const variantInput = restockWrap.querySelector('[data-agpb-nm-variant-input]');
          const productInput = restockWrap.querySelector('[data-agpb-nm-product-input]');
          const urlInput     = restockWrap.querySelector('[data-agpb-nm-url-input]');
          if (variantInput) variantInput.value = variantId  || '';
          if (productInput) productInput.value = productId  || '';
          if (urlInput)     urlInput.value     = productUrl || '';
          // Reset form state
          const emailInput = restockWrap.querySelector('input[name="email"]');
          if (emailInput) emailInput.value = '';
          const msgSuccess = restockWrap.querySelector('#dd-restock-notification-msg-success');
          const msgError   = restockWrap.querySelector('#dd-restock-notification-msg-error');
          if (msgSuccess) msgSuccess.style.display = 'none';
          if (msgError)   msgError.style.display   = 'none';
        } else {
          // ── Addon OOS: show link fallback ──────────────────────────
          if (restockWrap) restockWrap.style.display = 'none';
          if (linkWrap) linkWrap.style.display = '';
          const linkBtn = nmModal.querySelector('[data-agpb-nm-link]');
          if (linkBtn) {
            if (notifyLink) {
              linkBtn.href = notifyLink;
              linkBtn.removeAttribute('style');
            } else {
              linkBtn.removeAttribute('href');
              linkBtn.style.opacity = '0.4';
              linkBtn.style.cursor = 'not-allowed';
              linkBtn.style.pointerEvents = 'none';
            }
          }
        }
      }

      nmModal.classList.remove('is-hidden');
      document.body.style.overflow = 'hidden';
    }

    function closeNotifyModal() {
      if (!nmModal) return;
      nmModal.classList.add('is-hidden');
      document.body.style.overflow = '';
    }

    if (nmModal)    nmModal.addEventListener('click', e => { if (e.target.closest('[data-agpb-nm-cancel]')) closeNotifyModal(); });
    if (nmBackdrop) nmBackdrop.addEventListener('click', closeNotifyModal);

    // ── Instant visual feedback on DA Restock button ─────────────────
    // The app's own handler fires async; we intercept first in capture phase
    // to give immediate button state so the user knows the click registered.
    if (nmModal) {
      nmModal.addEventListener('click', e => {
        const btn = e.target.closest('.da-restock-notification-button');
        if (!btn) return;
        const emailInput = btn.closest('.da-restock-notification-form') && btn.closest('.da-restock-notification-form').querySelector('input[name="email"]');
        if (!emailInput || !emailInput.value.trim()) return; // let app handle empty-email case
        btn.textContent = 'Sending…';
        btn.style.opacity = '0.6';
        btn.style.pointerEvents = 'none';
        // Safety net: re-enable after 6s if the app does not update the DOM
        setTimeout(function() {
          if (btn.textContent === 'Sending…') {
            btn.textContent = 'Notify me when available';
            btn.style.opacity = '';
            btn.style.pointerEvents = '';
          }
        }, 6000);
      }, true); // capture phase: fires before the app's own bubble-phase listener
    }

    // ── Base selection ──────────────────────────────────────────────
    // ── URL sync ────────────────────────────────────────────────────
    function updateUrl() {
      const url = new URL(window.location.href);
      if (baseVariantId) {
        url.searchParams.set('variant', baseVariantId);
      } else {
        url.searchParams.delete('variant');
      }
      url.searchParams.delete('addon');
      getOrderedAddonSelections().forEach(({ sel }) => {
        if (sel.variantId) url.searchParams.append('addon', sel.variantId);
      });
      history.replaceState({}, '', url.toString());
    }

    function commitBaseSelection(wrap, btn, { silent = false } = {}) {
      baseItems.forEach(w => setActive(w.querySelector('.agpb__tile--base'), false));
      setActive(btn, true);
      const d       = getWrapData(wrap);
      baseVariantId = d.variantId;
      baseTitle     = d.title;
      basePrice     = d.price;
      renderBaseStatus(wrap);
      baseStage = '';
      if (d.followImage) showBasePhoto(d, silent);
      setError(null);
      if (silent) return;
      renderBasePrices(); // update base tile prices after selection change
      renderChips();
      renderTotal();
      refreshStickyVisibility();
      updateUrl();
    }

    function selectBase(wrap, btn) {
      if (!btn || btn.disabled) return;
      const d = getWrapData(wrap);

      if (d.status === 'preorder') {
        const alreadyAccepted = acceptPreorder && baseVariantId === d.variantId;
        if (alreadyAccepted) {
          commitBaseSelection(wrap, btn);
          return;
        }
        openStatusGate(
          d, wrap,
          () => { acceptPreorder = true; commitBaseSelection(wrap, btn); },
          () => { acceptPreorder = false; }
        );
      } else {
        acceptPreorder = false;
        commitBaseSelection(wrap, btn);
      }
    }

    // ── Add-on group gating ─────────────────────────────────────────
    /* A group carrying data-requires-group only shows once the group it names holds a
       qualifying selection (e.g. wheels only once a stand is picked).

       Hiding a group CLEARS its selection, and that is what does the real work: the
       total (computeTotalCents), the chips, the selection summary AND the cart lines
       (buildBundleItems) all read through getOrderedAddonSelections(), which skips
       empty selections. So there is exactly one place to get right, and a hidden
       group cannot leak into the price or the basket.

       Matching is by VARIANT ID, never by label or index. Renaming a tile or letting
       the theme editor reorder a group therefore cannot silently break a gate, the
       same class of bug as the spin-wheel positional prize_index failure.
    */
    function groupSelectedVariantIds(key) {
      const group = root.querySelector(`[data-agpb-group][data-group-key="${CSS.escape(key)}"]`);
      if (!group) return [];
      if (group.getAttribute('data-multi') === 'true') {
        return (addonMultiSelections.get(key) || []).filter(Boolean).map(s => Number(s.variantId));
      }
      const sel = addonSelections.get(key);
      return sel && sel.variantId ? [Number(sel.variantId)] : [];
    }

    function clearGroupSelection(group, key) {
      let cleared = false;
      if (group.getAttribute('data-multi') === 'true') {
        if ((addonMultiSelections.get(key) || []).length) { addonMultiSelections.set(key, []); cleared = true; }
      } else if (addonSelections.get(key)) {
        addonSelections.set(key, null); cleared = true;
      }
      if (cleared) {
        addonPreorderAccepted.delete(key);
        group.querySelectorAll('.agpb__tile--addon').forEach(b => setActive(b, false));
        renderAddonStatus(key, null);
      }
      return cleared;
    }

    /* Returns true if any selection was cleared, so the caller knows the total and
       chips must be re-rendered. */
    function applyGroupGating() {
      const gated = Array.from(root.querySelectorAll('[data-agpb-group][data-requires-group]'))
        .filter(g => (g.getAttribute('data-requires-group') || '').trim() !== '');
      if (!gated.length) return false;

      let cleared = false;
      /* Bounded passes so a chain (A gates B gates C) settles in one call, while a
         dependency CYCLE misconfigured in the theme editor cannot spin forever. */
      for (let pass = 0; pass < 4; pass++) {
        let flipped = false;
        gated.forEach(group => {
          const key    = group.getAttribute('data-group-key') || '';
          const depKey = (group.getAttribute('data-requires-group') || '').trim();
          const allowed = (group.getAttribute('data-requires-variants') || '')
            .split(',').map(s => Number(s.trim())).filter(Boolean);

          const depGroup  = root.querySelector(`[data-agpb-group][data-group-key="${CSS.escape(depKey)}"]`);
          // Depending on a group that is itself gated off is never satisfied.
          const depOff    = depGroup ? depGroup.hasAttribute('data-gated-off') : false;
          const depPicked = depOff ? [] : groupSelectedVariantIds(depKey);
          const satisfied = depPicked.length > 0 &&
            (allowed.length === 0 || depPicked.some(v => allowed.indexOf(v) > -1));

          const wasOff = group.hasAttribute('data-gated-off');
          if (satisfied === wasOff) flipped = true; // state changes on this pass

          if (satisfied) {
            group.removeAttribute('data-gated-off');
            group.hidden = false;
            group.style.display = '';
          } else {
            group.setAttribute('data-gated-off', '');
            group.hidden = true;
            /* Set display directly rather than relying on [hidden]: .agpb__blk carries
               its own display, which would win over the attribute. */
            group.style.display = 'none';
            if (clearGroupSelection(group, key)) cleared = true;
          }
        });
        if (!flipped) break;
      }
      return cleared;
    }

    // ── Addon selection ─────────────────────────────────────────────
    function commitAddonSelection(wrap, addonBtn, { silent = false } = {}) {
      const key      = wrap.getAttribute('data-group-key') || '';
      const group    = root.querySelector(`[data-agpb-group][data-group-key="${CSS.escape(key)}"]`);
      const multi    = group?.getAttribute('data-multi') === 'true';
      const d        = getWrapData(wrap);
      // updateAddonEffectivePrices mutates a tile's data-price to the SWITCHING COST (net − picked net)
      // when it's an unselected single-select option. Capturing that here would make the total under-count
      // after a switch. Always store the tile's true net (data-original-price) as the selection price.
      const _net = wrap.getAttribute('data-original-price');
      if (_net !== null && _net !== '') d.price = Number(_net);

      if (multi) {
        const sels = addonMultiSelections.get(key) || [];
        sels.push(d);
        // Sort by DOM position immediately so the Map is always in top-to-bottom order
        const domItems = Array.from(root.querySelectorAll(`[data-agpb-addon-item][data-group-key="${CSS.escape(key)}"]`));
        sels.sort((a, b) => {
          const ai = domItems.findIndex(el => Number(el.getAttribute('data-variant-id')) === a.variantId);
          const bi = domItems.findIndex(el => Number(el.getAttribute('data-variant-id')) === b.variantId);
          return ai - bi;
        });
        addonMultiSelections.set(key, sels);
        setActive(addonBtn, true);
      } else {
        addonSelections.set(key, d);
        group?.querySelectorAll('.agpb__tile--addon').forEach(b => setActive(b, false));
        setActive(addonBtn, true);
      }

      renderAddonStatus(key, wrap);
      if (d.followImage) renderMediaUrl(d.followImage, d.title);
      setError(null);
      if (silent) return;
      applyGroupGating();
      renderChips(); renderTotal();
      updateUrl();
    }

    function selectAddon(wrap, addonBtn) {
      const key      = wrap.getAttribute('data-group-key') || '';
      const group    = root.querySelector(`[data-agpb-group][data-group-key="${CSS.escape(key)}"]`);
      const required = group?.getAttribute('data-required') === 'true';
      const multi    = group?.getAttribute('data-multi') === 'true';
      const d        = getWrapData(wrap);

      if (!d.available) return;

      if (multi) {
        // Toggle this item on/off in the multi selection array
        const sels    = addonMultiSelections.get(key) || [];
        const idx     = sels.findIndex(s => s.variantId === d.variantId);
        if (idx > -1) {
          // Deselect
          sels.splice(idx, 1);
          addonMultiSelections.set(key, sels);
          setActive(addonBtn, false);
          renderAddonStatus(key, sels.length ? wrap : null);
          setError(null);
          applyGroupGating();
          renderChips(); renderTotal();
          updateUrl();
        } else {
          // Select, pre-order is the only status that gates on selection
          if (d.status === 'preorder') {
            openStatusGate(
              d, wrap,
              () => { addonPreorderAccepted.add(key); commitAddonSelection(wrap, addonBtn); },
              () => { addonPreorderAccepted.delete(key); }
            );
          } else {
            commitAddonSelection(wrap, addonBtn);
          }
        }
        return;
      }

      // ── Single-select (original logic) ──────────────────────────────
      const current = addonSelections.get(key) || null;

      // Toggle off if clicking the already-selected optional addon
      if (!required && current && current.variantId === d.variantId) {
        addonSelections.set(key, null);
        addonPreorderAccepted.delete(key);
        group?.querySelectorAll('.agpb__tile--addon').forEach(b => setActive(b, false));
        renderAddonStatus(key, null);
        setError(null);
        applyGroupGating();
        renderChips(); renderTotal();
        updateUrl();
        return;
      }

      if (d.status === 'preorder') {
        const alreadyAccepted = addonPreorderAccepted.has(key) && current?.variantId === d.variantId;
        if (alreadyAccepted) {
          commitAddonSelection(wrap, addonBtn);
          return;
        }
        openStatusGate(
          d, wrap,
          () => { addonPreorderAccepted.add(key);    commitAddonSelection(wrap, addonBtn); },
          () => { addonPreorderAccepted.delete(key); }
        );
      } else {
        addonPreorderAccepted.delete(key);
        commitAddonSelection(wrap, addonBtn);
      }
    }

    // ── Delegated click handler ─────────────────────────────────────
    root.addEventListener('click', e => {

      // Let learn more links pass through uninhibited, they sit inside addon wrappers
      // which would otherwise be captured by the addon selection handler
      if (e.target.closest('.agpb__learnMore')) return;

      const notifyTrigger = e.target.closest('[data-agpb-notify-trigger]');
      if (notifyTrigger) {
        const t = notifyTrigger.getAttribute('data-notify-title')  || '';
        const s = notifyTrigger.getAttribute('data-notify-status') || '';
        openNotifyModal(t, s === 'preorder' ? 'Pre-order' : 'Out of stock', notifyTrigger.getAttribute('data-notify-link') || '');
        return;
      }

      const baseBtn = e.target.closest('.agpb__tile--base');
      if (baseBtn) {
        // If disabled (OOS), bubble up to wrapper to handle Notify me
        if (baseBtn.disabled) {
          const wrap = baseBtn.closest('[data-agpb-base-item]');
          if (!wrap) return;
          const d = getWrapData(wrap);
          openNotifyModal(d.title, 'Out of stock', d.notifyLink, true, d.variantId, d.productId, d.productUrl);
          return;
        }
        const wrap = baseBtn.closest('[data-agpb-base-item]');
        if (!wrap) return;
        selectBase(wrap, baseBtn);
        return;
      }

      // Wrapper click for OOS base items (tile is disabled, wrapper is not)
      const baseWrap = e.target.closest('[data-agpb-base-item]');
      if (baseWrap && baseWrap.getAttribute('data-available') === 'false') {
        const d = getWrapData(baseWrap);
        openNotifyModal(d.title, 'Out of stock', d.notifyLink, true, d.variantId, d.productId, d.productUrl);
        return;
      }

      const addonBtn = e.target.closest('.agpb__tile--addon');
      if (addonBtn) {
        const wrap = addonBtn.closest('[data-agpb-addon-item]');
        if (!wrap) return;
        // OOS: tile has pointer-events:none, but just in case, open Notify me
        if (addonBtn.disabled) {
          const d = getWrapData(wrap);
          openNotifyModal(d.title, 'Out of stock', d.notifyLink);
          return;
        }
        selectAddon(wrap, addonBtn);
        return;
      }

      // Wrapper click for OOS addon items (tile is pointer-events:none, wrapper catches it)
      const addonWrap = e.target.closest('[data-agpb-addon-item]');
      if (addonWrap && addonWrap.getAttribute('data-available') === 'false') {
        const d = getWrapData(addonWrap);
        openNotifyModal(d.title, 'Out of stock', d.notifyLink);
        return;
      }

      const dec      = e.target.closest('[data-agpb-qty-dec]');
      const qtyInput = root.querySelector('[data-agpb-qty]');
      if (dec && qtyInput) {
        qty = Math.max(1, Number(qtyInput.value || '1') - 1);
        qtyInput.value = String(qty);
        renderTotal();
        return;
      }

      const inc = e.target.closest('[data-agpb-qty-inc]');
      if (inc && qtyInput) {
        qty = Math.max(1, Number(qtyInput.value || '1') + 1);
        qtyInput.value = String(qty);
        renderTotal();
        return;
      }
    });

    const qtyInput = root.querySelector('[data-agpb-qty]');
    if (qtyInput) {
      qtyInput.addEventListener('change', () => {
        qty = Math.max(1, Number(qtyInput.value || '1'));
        qtyInput.value = String(qty);
        renderTotal();
      });
    }

    // ── Selections helpers ─────────────────────────────────────────
    function getTitleText(el) {
      if (!el) return '';
      const clone = el.cloneNode(true);
      clone.querySelectorAll('.agpb__hintInline').forEach(n => n.remove());
      return clone.textContent.trim();
    }

    function getProductLabel() {
      const el = root.querySelector('[data-agpb-product-label]');
      return (el ? (el.textContent || '').trim() : '') || 'Product';
    }

    // Returns addon selections in DOM order (editor order), not insertion order
    function getOrderedAddonSelections() {
      const groups = Array.from(root.querySelectorAll('[data-agpb-group][data-group-key]'));
      const result = [];
      groups.forEach(group => {
        const key   = group.getAttribute('data-group-key');
        const multi = group.getAttribute('data-multi') === 'true';
        if (multi) {
          const sels = addonMultiSelections.get(key) || [];
          sels.forEach(sel => result.push({ key, sel, group }));
        } else {
          const sel = addonSelections.get(key);
          if (sel) result.push({ key, sel, group });
        }
      });
      return result;
    }

    function getSelectionsLines() {
      const lines = [];
      if (baseTitle) lines.push(`${getProductLabel()}: ${baseTitle}`);
      getOrderedAddonSelections().forEach(({ sel, group }) => {
        const gTitle = getTitleText(group?.querySelector('.agpb__title')) || 'Add-on';
        lines.push(`${gTitle}: ${sel.title}`);
      });
      return lines;
    }

    // Marker for gated statuses in the summary UI (chips / sticky thumbs /
    // composite cart image): red Pre-order, amber merchant-set custom label
    // (e.g. End of Line). Returns null for normal items.
    function statusFlag(status, customLabel, customColor) {
      if (status === 'preorder') return { text: 'Pre-order', color: '#a30d26', cls: 'preorder' };
      if (status === 'custom')   return { text: (customLabel || '').trim() || 'End of Line', color: (customColor || '').trim() || '#b45309', cls: 'custom' };
      return null;
    }
    function escHtml(s) { return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;'); }

    function renderChips() {
      renderCompositeCanvas();
      const chipsHost = root.querySelector('[data-agpb-chips]');
      if (!chipsHost) return;
      chipsHost.innerHTML = '';
      const items = [];
      if (baseTitle) {
        const baseWrap = baseItems.find(w => Number(w.getAttribute('data-variant-id')) === baseVariantId);
        let img = baseWrap ? baseWrap.getAttribute('data-follow-image') : '';
        if (!img && mediaStage) { const mi = mediaStage.querySelector('img'); if (mi) img = mi.src; }
        const baseStatus = baseWrap ? (baseWrap.getAttribute('data-status') || '') : '';
        items.push({ group: getProductLabel(), item: baseTitle, img, flag: statusFlag(baseStatus, baseWrap ? baseWrap.getAttribute('data-custom-label') : '', baseWrap ? baseWrap.getAttribute('data-custom-color') : '') });
      }
      getOrderedAddonSelections().forEach(({ sel, group }) => {
        const gTitle = getTitleText(group?.querySelector('.agpb__title')) || 'Add-on';
        items.push({ group: gTitle, item: sel.title, img: sel.followImage || '', flag: statusFlag(sel.status || '', sel.customLabel, sel.customColor) });
      });
      items.forEach(({ group, item, img, flag }) => {
        const el = document.createElement('div');
        el.className = 'agpb__chipOut' + (flag ? ' agpb__chipOut--' + flag.cls : '');
        if (flag && flag.cls === 'custom' && flag.color) el.style.setProperty('--agpb-custom-color', flag.color);
        const ribbonHtml = flag ? '<span class="agpb__chipRibbon" aria-hidden="true">' + escHtml(flag.text) + '</span>' : '';
        el.innerHTML = ribbonHtml + (img
          ? '<img class="agpb__chipThumb" src="' + escHtml(img) + '" alt="' + escHtml(item) + '">'
            + '<span class="agpb__chipText"><span class="agpb__chipGroup">' + escHtml(group) + '</span><span class="agpb__chipItem">' + escHtml(item) + '</span></span>'
          : '<span class="agpb__chipText"><span class="agpb__chipGroup">' + escHtml(group) + '</span><span class="agpb__chipItem">' + escHtml(item) + '</span></span>');
        chipsHost.appendChild(el);
      });
      const selBlk = root.querySelector('[data-agpb-selection]');
      if (selBlk) selBlk.classList.toggle('is-hidden', chipsHost.children.length === 0);
    }

    function renderAtcSelections() {
      const atcWrap = root.querySelector('[data-agpb-atc]');
      if (!atcWrap) return;
      if (atcWrap.getAttribute('data-show-atc-selections') === 'true') {
        const host = atcWrap.querySelector('[data-agpb-atc-selections]');
        if (host) {
          const lines = getSelectionsLines();
          host.textContent = lines.length ? lines.join(' • ') : '';
        }
      }
      const sticky     = root.querySelector('[data-agpb-sticky-atc]');
      const stickyShow = sticky?.getAttribute('data-show-sticky-selections') === 'true';
      const stickyHost = root.querySelector('[data-agpb-sticky-selections]');
      if (stickyShow && stickyHost) {
        const lines = getSelectionsLines();
        stickyHost.textContent = lines.length ? lines.join(' • ') : '';
      }

      // ── Sticky thumbnail strip ──────────────────────────────────────
      const thumbStrip = root.querySelector('[data-agpb-sticky-thumbs]');
      const thumbWrap  = root.querySelector('[data-agpb-sticky-thumb-wrap]');
      const thumbPrev  = root.querySelector('[data-agpb-thumb-prev]');
      const thumbNext  = root.querySelector('[data-agpb-thumb-next]');

      function updateThumbArrows() {
        if (!thumbStrip || !thumbPrev || !thumbNext) return;
        const canScrollLeft  = thumbStrip.scrollLeft > 2;
        const canScrollRight = thumbStrip.scrollLeft + thumbStrip.clientWidth < thumbStrip.scrollWidth - 2;
        thumbPrev.classList.toggle('is-hidden', !canScrollLeft);
        thumbNext.classList.toggle('is-hidden', !canScrollRight);
      }

      if (thumbStrip && !thumbStrip._arrowsInited) {
        thumbStrip._arrowsInited = true;
        thumbStrip.addEventListener('scroll', updateThumbArrows, { passive: true });
        if (thumbPrev) thumbPrev.addEventListener('click', () => {
          thumbStrip.scrollBy({ left: -80, behavior: 'smooth' });
        });
        if (thumbNext) thumbNext.addEventListener('click', () => {
          thumbStrip.scrollBy({ left: 80, behavior: 'smooth' });
        });
      }

      if (thumbStrip) {
        thumbStrip.innerHTML = '';
        const imgs = [];
        if (baseVariantId) {
          const baseWrap = baseItems.find(w => Number(w.getAttribute('data-variant-id')) === baseVariantId);
          let img = baseWrap ? baseWrap.getAttribute('data-follow-image') : '';
          if (!img && mediaStage) { const mi = mediaStage.querySelector('img'); if (mi) img = mi.src; }
          const baseStatus = baseWrap ? (baseWrap.getAttribute('data-status') || '') : '';
          if (img) imgs.push({ img, title: baseTitle, group: getProductLabel(), flag: statusFlag(baseStatus, baseWrap ? baseWrap.getAttribute('data-custom-label') : '', baseWrap ? baseWrap.getAttribute('data-custom-color') : '') });
        }
        getOrderedAddonSelections().forEach(({ sel, group }) => {
          const gTitle = getTitleText(group?.querySelector('.agpb__title')) || 'Add-on';
          if (sel.followImage) imgs.push({ img: sel.followImage, title: sel.title, group: gTitle, flag: statusFlag(sel.status || '', sel.customLabel, sel.customColor) });
        });
        const hasItems = imgs.length > 0;
        if (thumbWrap) thumbWrap.classList.toggle('is-hidden', !hasItems);
        imgs.forEach(item => {
          const wrap = document.createElement('div');
          wrap.className = 'agpb__stickyThumbItem' + (item.flag ? ' agpb__stickyThumbItem--' + item.flag.cls : '');
          if (item.flag && item.flag.cls === 'custom' && item.flag.color) wrap.style.setProperty('--agpb-custom-color', item.flag.color);
          wrap.innerHTML = '<img class="agpb__stickyThumb" src="' + escHtml(item.img) + '" alt="' + escHtml(item.title) + '" title="' + escHtml(item.title) + '">'
            + '<span class="agpb__stickyThumbText">'
            + '<span class="agpb__stickyThumbGroup">' + escHtml(item.group) + '</span>'
            + '<span class="agpb__stickyThumbName">' + escHtml(item.title) + '</span>'
            + '</span>';
          thumbStrip.appendChild(wrap);
        });
        // Always scroll back to start so base product is never cut off
        thumbStrip.scrollLeft = 0;
        // Update arrows once layout is stable - double rAF + timeout fallback
        requestAnimationFrame(() => requestAnimationFrame(updateThumbArrows));
        setTimeout(updateThumbArrows, 300);
        // Also update when each image finishes loading
        thumbStrip.querySelectorAll('img').forEach(img => {
          if (!img.complete) img.addEventListener('load', updateThumbArrows, { once: true });
        });
      }
    }

    // ── Price / total ───────────────────────────────────────────────
    function computeTotalCents() {
      let total = Number(basePrice || 0);
      getOrderedAddonSelections().forEach(({ sel }) => { total += Number(sel.price || 0); });
      return total * Number(qty || 1);
    }

    // ── Storefront API price (with automatic discounts) ─────────────
    const _sfToken  = cfg.sfToken;
    const _sfShop   = cfg.sfShop;
    const _sfApiVer = '2026-04';

    // ── Storefront API result cache ──────────────────────────────────
    // Keyed by sorted variant+quantity combinations so order doesn't matter.
    // Only successful responses are cached, failures always hit the API fresh.
    // Scoped to this section instance via the closure.
    const _sfCache = new Map();

    function _sfCacheKey(lineItems) {
      return lineItems
        .map(li => `${li.variantId}:${li.quantity}`)
        .sort()
        .join('|');
    }

    async function fetchDiscountedTotal(lineItems) {
      // lineItems: [{ variantId, quantity }]
      // Uses cartCreate (current API), checkoutCreate is deprecated and
      // does not reliably apply automatic discounts on newer API versions.

      // Check cache first, only serve if we have a confirmed result
      const cacheKey = _sfCacheKey(lineItems);
      if (_sfCache.has(cacheKey)) return _sfCache.get(cacheKey);

      const query = `
        mutation cartCreate($input: CartInput!) {
          cartCreate(input: $input) {
            cart {
              cost {
                totalAmount { amount currencyCode }
                subtotalAmount { amount }
              }
              lines(first: 50) { nodes { quantity } }
            }
            userErrors { message field }
          }
        }
      `;
      const variables = {
        input: {
          lines: lineItems.map(li => ({
            merchandiseId: `gid://shopify/ProductVariant/${li.variantId}`,
            quantity      : li.quantity
          }))
        }
      };
      const res = await fetch(
        `https://${_sfShop}/api/${_sfApiVer}/graphql.json`,
        {
          method : 'POST',
          headers: {
            'Content-Type'                    : 'application/json',
            'X-Shopify-Storefront-Access-Token': _sfToken
          },
          body: JSON.stringify({ query, variables }),
          signal: _timeoutSig(8000)
        }
      );
      if (!res.ok) throw new Error('Storefront API HTTP ' + res.status);
      const data = await res.json();
      const errors = data?.data?.cartCreate?.userErrors;
      if (errors && errors.length) console.warn('[AGPB] cartCreate userErrors:', errors);
      const cart = data?.data?.cartCreate?.cart;
      if (!cart) throw new Error('No cart returned');
      // Shopify's cartCreate silently CLAMPS out-of-stock / non-purchasable lines
      // (e.g. a pre-order variant with no on-hand inventory) to quantity 0 and returns
      // a €0 total with NO userError. Trusting that would paint the tile as 0 even
      // though the variant has a real price. Treat a short cart (returned qty < requested)
      // as a failure so the caller falls back to the Liquid-rendered price.
      const requestedQty = lineItems.reduce((s, li) => s + (li.quantity || 0), 0);
      const returnedQty  = (cart.lines?.nodes || []).reduce((s, n) => s + (n.quantity || 0), 0);
      if (returnedQty < requestedQty) throw new Error('Cart line clamped, variant not purchasable');
      // Return total in cents, only cache on confirmed success
      const total = Math.round(parseFloat(cart.cost.totalAmount.amount) * 100);
      _sfCache.set(cacheKey, total);
      return total;
    }

    function renderAtcButtonTotals(total) {
      [
        { btn: root.querySelector('[data-agpb-atc-btn]'), totalSel: '[data-agpb-atc-total]', sepSel: '[data-agpb-atc-sep]' },
        { btn: root.querySelector('[data-agpb-sticky-btn]'), totalSel: '[data-agpb-sticky-btn-total]', sepSel: '[data-agpb-sticky-btn-sep]' }
      ].forEach(({ btn, totalSel, sepSel }) => {
        if (!btn) return;
        const show    = btn.getAttribute('data-show-total-in-btn') === 'true';
        const totalEl = btn.querySelector(totalSel);
        const sepEl   = btn.querySelector(sepSel);
        if (!totalEl || !sepEl) return;
        if (show) {
          totalEl.textContent = formatMoney(total);
          totalEl.classList.remove('is-hidden');
          sepEl.classList.remove('is-hidden');
        } else {
          totalEl.textContent = '';
          totalEl.classList.add('is-hidden');
          sepEl.classList.add('is-hidden');
        }
      });
    }

    function applyTotal(totalCents) {
      const totalEl = root.querySelector('[data-agpb-total-cents]');
      if (totalEl) totalEl.textContent = formatMoney(totalCents);
      renderAtcButtonTotals(totalCents);
      const stickyTotal = root.querySelector('[data-agpb-sticky-total]');
      if (stickyTotal) stickyTotal.textContent = formatMoney(totalCents);
      renderFinance(totalCents);
      renderFinanceCards(totalCents);
      renderSavings(totalCents);
    }

    function renderSavings(totalCents) {
      const savingsRow    = root.querySelector('[data-agpb-savings]');
      const savingsText   = root.querySelector('[data-agpb-savings-text]');
      const stickySavings = root.querySelector('[data-agpb-sticky-savings]');

      // Sum compare-at prices for selected base + addons
      let compareTotal = 0;
      const activeBase = root.querySelector('[data-agpb-base-item] .agpb__tile--base.is-active');
      if (activeBase) {
        const baseWrap = activeBase.closest('[data-agpb-base-item]');
        const cap = Number(baseWrap?.getAttribute('data-compare-at-price') || 0);
        compareTotal += cap > 0 ? cap : Number(baseWrap?.getAttribute('data-price') || 0);
      }
      root.querySelectorAll('[data-agpb-addon-item] .agpb__tile--addon.is-active').forEach(btn => {
        const wrap = btn.closest('[data-agpb-addon-item]');
        const cap = Number(wrap?.getAttribute('data-compare-at-price') || 0);
        compareTotal += cap > 0 ? cap : Number(wrap?.getAttribute('data-price') || 0);
      });

      compareTotal *= Number(qty || 1);
      const saved = compareTotal - totalCents;

      const show = saved > 0;
      const label = show ? 'You save ' + formatMoney(saved) : '';

      if (savingsRow && savingsText) {
        savingsText.textContent = label;
        savingsRow.classList.toggle('is-hidden', !show);
      }
      if (stickySavings) {
        stickySavings.textContent = label;
        stickySavings.classList.toggle('is-hidden', !show);
      }
    }

    function renderTotal() {
      renderAtcSelections();

      const stickyTitle = root.querySelector('[data-agpb-sticky-title]');
      if (stickyTitle && baseTitle) stickyTitle.textContent = baseTitle;

      const fin = root.querySelector('[data-agpb-finance]');
      if (fin) fin.classList.toggle('is-hidden', !baseVariantId);

      // Config-driven pricing. The automatic discount CODES are gone, add-on savings now live in
      // builder_config (Liquid renders each tile's NET price = real − saving into data-price) and are
      // applied at checkout by the discount FUNCTION. So compute the tile prices + the total locally
      // from those nets, no speculative cart round-trip (which could no longer see the saving anyway).
      // Base prices (incl. any standalone sale) are still resolved separately by updateBasePrices().
      updateAddonEffectivePrices();
      applyTotal(computeTotalCents());
    }

    // ── Base product price update (Storefront API) ───────────────────
    // Fires once on init, each base tile gets a standalone cartCreate call.
    // Discounted prices are cached, then each tile shows its own actual price.
    const _basePriceCache = new Map(); // variantId → discounted cents

    function renderBasePrices() {
      if (!_basePriceCache.size) return;
      baseItems.forEach(wrap => {
        const variantId = Number(wrap.getAttribute('data-variant-id'));
        const priceEl   = wrap.querySelector('.agpb__basePrice .agpb__money:not(.agpb__money--compare)');
        if (!priceEl) return;

        const ownPrice = _basePriceCache.get(variantId);
        if (ownPrice === undefined) return;

        priceEl.textContent = formatMoney(ownPrice);
      });
    }

    async function updateBasePrices() {
      if (!_sfToken) return;

      const availableBaseWraps = baseItems.filter(w => w.getAttribute('data-available') === 'true');
      if (!availableBaseWraps.length) return;

      const updates = availableBaseWraps.map(async wrap => {
        const variantId = Number(wrap.getAttribute('data-variant-id'));
        const priceEl   = wrap.querySelector('.agpb__basePrice .agpb__money:not(.agpb__money--compare)');

        // Show shimmer while fetching
        if (priceEl) {
          priceEl.setAttribute('data-agpb-loading', 'true');
          priceEl.textContent = '-';
        }

        try {
          const discountedTotal = await fetchDiscountedTotal([{ variantId, quantity: 1 }]);
          _basePriceCache.set(variantId, discountedTotal);
          // Keep data-price in sync so computeTotalCents fallback is accurate
          wrap.setAttribute('data-price', String(discountedTotal));
          if (baseVariantId === variantId) basePrice = discountedTotal;
          if (priceEl) priceEl.removeAttribute('data-agpb-loading');
        } catch(e) {
          // Fall back to Liquid-rendered price
          const orig = wrap.getAttribute('data-original-price') || wrap.getAttribute('data-price') || '0';
          _basePriceCache.set(variantId, Number(orig));
          if (priceEl) priceEl.removeAttribute('data-agpb-loading');
        }
      });

      await Promise.allSettled(updates);
      // Once all prices are fetched, render diffs from the now-complete cache
      renderBasePrices();
      // And repaint the total row: basePrice may have moved to the resolved
      // (e.g. sale) price above, and nothing else re-renders it until the next
      // interaction, so tiles and total disagreed in that window.
      // (Ported from EU ag-product-builder.js, which has had this since PR #70.)
      renderTotal();
    }

    // Add-on tile prices, computed directly from builder_config, no discount round-trip.
    // Each tile's NET cost = real price − saving (Liquid-rendered into data-price, cached once as
    // data-original-price). Displays: selected → its net contribution; unselected in a multi group →
    // its net; unselected single-select with another item picked → the SWITCHING cost (net − the
    // picked item's net, can be negative = saving); unselected single-select with nothing picked → its net.
    function updateAddonEffectivePrices() {
      const allAddonWraps = Array.from(root.querySelectorAll('[data-agpb-addon-item]'))
        .filter(w => w.getAttribute('data-available') === 'true');
      if (!allAddonWraps.length) return;

      // Cache the static Liquid-rendered net (real − saving) once.
      allAddonWraps.forEach(w => {
        if (!w.hasAttribute('data-original-price')) {
          w.setAttribute('data-original-price', w.getAttribute('data-price') || '0');
        }
      });

      const selectedAddonIds = new Set();
      getOrderedAddonSelections().forEach(({ sel }) => { if (sel && sel.variantId) selectedAddonIds.add(sel.variantId); });

      allAddonWraps.forEach(wrap => {
        const variantId  = Number(wrap.getAttribute('data-variant-id'));
        const groupKey   = wrap.getAttribute('data-group-key') || '';
        const groupEl    = root.querySelector(`[data-agpb-group][data-group-key="${CSS.escape(groupKey)}"]`);
        const isMulti    = groupEl?.getAttribute('data-multi') === 'true';
        const net        = Number(wrap.getAttribute('data-original-price') || '0');
        const isSelected = selectedAddonIds.has(variantId);

        let displayCost;
        if (isSelected || isMulti) {
          displayCost = net; // its own net contribution / marginal cost
        } else {
          const groupSelectedId = addonSelections.get(groupKey)?.variantId || null;
          if (groupSelectedId) {
            const selWrap = root.querySelector(`[data-agpb-addon-item][data-variant-id="${groupSelectedId}"]`);
            const selNet  = selWrap ? Number(selWrap.getAttribute('data-original-price') || '0') : 0;
            displayCost   = net - selNet; // switching cost (can be negative = saving)
          } else {
            displayCost = net; // standalone
          }
        }

        // data-price drives computeTotalCents, selected tiles contribute their net.
        wrap.setAttribute('data-price', String(isSelected ? net : displayCost));

        const priceEl  = wrap.querySelector('.agpb__priceMain .agpb__money');
        const prefixEl = wrap.querySelector('.agpb__priceMain .agpb__prefix');
        if (priceEl) {
          priceEl.removeAttribute('data-agpb-loading');
          priceEl.textContent = formatMoney(Math.abs(displayCost));
          if (prefixEl) prefixEl.textContent = (!isSelected && displayCost < 0) ? '−' : '+';
        }
      });
    }

    // ── Live add-on price reconcile (self-heal a stale server render) ─
    // Tile prices above are baked into the page HTML at render time from
    // builder_config. Shopify's full-page CDN cache can serve a visitor HTML
    // that was rendered BEFORE a merchant changed an add-on saving in the
    // Bundle Builder, so a tile can show a stale "+£X / Was £Y" while the cart
    // (priced live by the discount function) is already correct. This re-fetches
    // THIS section fresh with a cache-buster, forcing Shopify to re-render the
    // Liquid against the CURRENT metafield, and transplants any tile whose net
    // price has moved. Best-effort + silent: any failure leaves the baked prices.
    let _agpbReconciled = false;
    async function reconcileAddonPricesLive() {
      if (_agpbReconciled) return;
      if (window.Shopify && window.Shopify.designMode) return;   // theme editor renders differently
      if (!root.querySelector('[data-agpb-addon-item]')) return; // no add-ons → nothing to reconcile
      _agpbReconciled = true;

      const sectionId = root.getAttribute('data-agpb-section-id');
      if (!sectionId) return;

      // One origin re-render per 5-minute bucket per page is enough healing:
      // the staleness this closes is CDN-cache-length, not seconds. The old
      // Date.now() buster forced a full origin render on EVERY addon-PDP view.
      const _recBucket = Math.floor(Date.now() / 300000);
      const _recKey = 'agpbRec:' + sectionId + ':' + location.pathname;
      try { if (sessionStorage.getItem(_recKey) === String(_recBucket)) return; } catch (e) {}

      try {
        const url = location.pathname
          + '?section_id=' + encodeURIComponent(sectionId)
          + '&_agpb=' + _recBucket; // bucketed param: repeat views inside the bucket hit the CDN
        const res = await fetch(url, { headers: { 'X-Requested-With': 'fetch' }, credentials: 'same-origin', signal: _timeoutSig(8000) });
        if (!res.ok) return;
        try { sessionStorage.setItem(_recKey, String(_recBucket)); } catch (e) {}
        const fresh = new DOMParser().parseFromString(await res.text(), 'text/html');

        let changed = false;
        root.querySelectorAll('[data-agpb-addon-item]').forEach(wrap => {
          const vid = wrap.getAttribute('data-variant-id');
          if (!vid) return;
          const freshWrap = fresh.querySelector('[data-agpb-addon-item][data-variant-id="' + CSS.escape(vid) + '"]');
          if (!freshWrap) return;

          const freshNet     = freshWrap.getAttribute('data-price')             || '0';
          const freshSave    = freshWrap.getAttribute('data-saving')            || '0';
          const freshCompare = freshWrap.getAttribute('data-compare-at-price')  || '0';

          // Compare against the TRUE baked net (data-original-price is cached
          // before updateAddonEffectivePrices mutates data-price into a switching cost).
          const bakedNet  = wrap.getAttribute('data-original-price') || wrap.getAttribute('data-price') || '0';
          const bakedSave = wrap.getAttribute('data-saving') || '0';
          if (freshNet === bakedNet && freshSave === bakedSave) return; // already fresh

          // Adopt the fresh authoritative values.
          wrap.setAttribute('data-price', freshNet);
          wrap.setAttribute('data-original-price', freshNet);
          wrap.setAttribute('data-saving', freshSave);
          wrap.setAttribute('data-compare-at-price', freshCompare);

          // Transplant the price block so the "Was …" line appears/disappears
          // correctly with show_compare; renderStaticMoney() refills the money text.
          const liveP  = wrap.querySelector('.agpb__tilePrice');
          const freshP = freshWrap.querySelector('.agpb__tilePrice');
          if (liveP && freshP) liveP.innerHTML = freshP.innerHTML;
          changed = true;
        });

        if (!changed) return;

        // Refresh any already-restored selections so a live cart-add carries the
        // corrected saving to the discount engine.
        addonSelections.forEach((sel, key) => {
          if (!sel || !sel.variantId) return;
          const w = root.querySelector('[data-agpb-addon-item][data-variant-id="' + CSS.escape(String(sel.variantId)) + '"]');
          if (w) addonSelections.set(key, getWrapData(w));
        });
        addonMultiSelections.forEach((arr, key) => {
          if (!Array.isArray(arr)) return;
          addonMultiSelections.set(key, arr.map(sel => {
            if (!sel || !sel.variantId) return sel;
            const w = root.querySelector('[data-agpb-addon-item][data-variant-id="' + CSS.escape(String(sel.variantId)) + '"]');
            return w ? getWrapData(w) : sel;
          }));
        });

        renderStaticMoney(); // fill data-money-cents text on the transplanted blocks
        renderTotal();       // reruns updateAddonEffectivePrices + total + savings row
      } catch (e) { /* best-effort, keep the baked-in prices */ }
    }

    // ── Finance ─────────────────────────────────────────────────────
    let financeMonths = cfg.financeMonths;
    let _renderRepExFn = null; // set by initFinance, called by renderFinance on price change

    function renderFinance(totalCents) {
      const fin = root.querySelector('[data-agpb-finance]');
      if (!fin) return;
      const perMoEl = fin.querySelector('[data-agpb-fin-permo-cents]');
      if (!perMoEl) return;

      const total  = totalCents !== undefined ? totalCents : computeTotalCents();
      const term   = Number(financeMonths || 12);
      const suffix = fin.querySelector('.agpb__finFromSuffix');

      if (term === 30) {
        perMoEl.textContent = formatMoney(total);
        if (suffix) suffix.textContent = 'due in 30 days';
        if (_renderRepExFn) _renderRepExFn(term);
        return;
      }

      if (suffix) suffix.textContent = '/mo';
      perMoEl.textContent = formatMoney(term > 0 ? Math.ceil(total / term) : total);
      if (_renderRepExFn) _renderRepExFn(term);
    }

    function initFinance() {
      const fin = root.querySelector('[data-agpb-finance]');
      if (!fin) return;

      const chips          = fin.querySelectorAll('[data-agpb-month]');
      const providersHost  = root.querySelector('[data-agpb-fin-providers]');
      const providersJson  = root.querySelector('[data-agpb-finance-providers]');

      let providersMap = {};
      if (providersJson) {
        try { providersMap = JSON.parse(providersJson.textContent || '{}') || {}; } catch(e) {}
      }

      function escHtml(s) {
        return String(s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
      }
      function prettyKey(k) {
        const s = String(k || '').trim();
        return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
      }

      const checkoutUrlsEl  = fin.querySelector('[data-agpb-finance-checkout-urls]');
      let checkoutUrls = {};
      if (checkoutUrlsEl) {
        try { checkoutUrls = JSON.parse(checkoutUrlsEl.textContent || '{}') || {}; } catch(e) {}
      }

      const finApplyBtn = fin.querySelector('[data-agpb-fin-checkout-btn]');
      if (finApplyBtn) {
        finApplyBtn.addEventListener('click', () => {
          if (!baseVariantId) {
            setError('Please select a product before checking out.');
            return;
          }
          openFinModal();
        });
      }

      function renderProviders(term) {
        if (!providersHost) return;
        const arr   = Array.isArray(providersMap[String(term)]) ? providersMap[String(term)] : [];
        const items = arr.filter(x => x && x.key);

        // Provider logos, static display only
        if (!items.length) {
          providersHost.innerHTML = '';
          if (finApplyBtn) finApplyBtn.classList.add('is-hidden');
          return;
        }

        providersHost.innerHTML = items.map(p => {
          const label = prettyKey(p.key);
          const logo  = p.logo ? `<img class="agpb__finLogo" src="${escHtml(p.logo)}" alt="${escHtml(label)}">` : '';
          return `<span class="agpb__finProv" role="text">${logo}<span class="agpb__finProvText">${escHtml(label)}</span></span>`;
        }).join('');

        // Show the apply button
        if (finApplyBtn) finApplyBtn.classList.remove('is-hidden');
      }

      function renderDesc(term) {
        const descHost = fin.querySelector('[data-agpb-fin-desc]');
        if (!descHost) return;
        const chip = fin.querySelector(`[data-agpb-month="${term}"]`);
        const text = chip ? (chip.getAttribute('data-fin-desc') || '').trim() : '';
        if (text) {
          descHost.textContent = text;
          descHost.classList.remove('is-hidden');
        } else {
          descHost.classList.add('is-hidden');
        }
      }

      // ── Info tooltips (rep example + credit statement) ──────────────
      function initFinTooltip(triggerBtn, tooltipEl) {
        if (!triggerBtn || !tooltipEl) return;
        function toggle(e) {
          e.stopPropagation();
          const open = !tooltipEl.classList.contains('is-hidden');
          // Close all other tooltips first
          fin.querySelectorAll('.agpb__finTooltip').forEach(t => t.classList.add('is-hidden'));
          fin.querySelectorAll('.agpb__finInfoBtn').forEach(b => b.setAttribute('aria-expanded', 'false'));
          if (!open) {
            tooltipEl.classList.remove('is-hidden');
            triggerBtn.setAttribute('aria-expanded', 'true');
          }
        }
        triggerBtn.addEventListener('click', toggle);
        triggerBtn.addEventListener('touchend', e => { e.preventDefault(); toggle(e); });
      }

      // Inject rep example icon from template
      const repExTmpl = fin.querySelector('[data-agpb-fin-rep-ex-template]');
      const repExSlot = fin.querySelector('[data-agpb-fin-rep-ex-icon-slot]');
      let repExBtn = null, repExTip = null, repExText = null;
      if (repExTmpl && repExSlot) {
        const clone = repExTmpl.content.cloneNode(true);
        repExSlot.appendChild(clone);
        repExBtn  = repExSlot.querySelector('[data-agpb-fin-rep-ex-btn]');
        repExTip  = repExSlot.querySelector('[data-agpb-fin-rep-ex-tip]');
        repExText = repExSlot.querySelector('[data-agpb-fin-rep-ex-text]');
        initFinTooltip(repExBtn, repExTip);
      }

      // Inject credit statement icon from template
      const creditTmpl = fin.querySelector('[data-agpb-fin-credit-template]');
      const creditSlot = fin.querySelector('[data-agpb-fin-credit-icon-slot]');
      if (creditTmpl && creditSlot) {
        const clone = creditTmpl.content.cloneNode(true);
        creditSlot.appendChild(clone);
        const creditBtn = creditSlot.querySelector('[data-agpb-fin-credit-btn]');
        const creditTip = creditSlot.querySelector('[data-agpb-fin-credit-tip]');
        initFinTooltip(creditBtn, creditTip);
      }

      // Close tooltips when clicking outside
      document.addEventListener('click', () => {
        fin.querySelectorAll('.agpb__finTooltip').forEach(t => t.classList.add('is-hidden'));
        fin.querySelectorAll('.agpb__finInfoBtn').forEach(b => b.setAttribute('aria-expanded', 'false'));
      });

      function renderRepEx(term) {
        if (!repExBtn || !repExTip || !repExText) return;

        // Pay in 30 is currently unregulated BNPL, hide icon
        if (term === 30) {
          repExBtn.classList.add('is-hidden');
          repExTip.classList.add('is-hidden');
          return;
        }

        const total    = computeTotalCents();
        const perMonth = term > 0 ? Math.ceil(total / term) : total;

        repExText.textContent =
          `Cash price ${formatMoney(total)}. No deposit. ` +
          `${term} monthly payments of ${formatMoney(perMonth)}. ` +
          `Total amount payable ${formatMoney(perMonth * term)}. ` +
          `0% APR representative.`;

        repExBtn.classList.remove('is-hidden');
      }

      function setMonths(m) {
        financeMonths = Number(m || 12);
        chips.forEach(b => {
          const bm = Number(b.getAttribute('data-agpb-month') || '0');
          b.classList.toggle('is-active', bm === financeMonths);
          b.setAttribute('aria-pressed', bm === financeMonths ? 'true' : 'false');
        });
        renderFinance();
        renderProviders(financeMonths);
        renderDesc(financeMonths);
        renderRepEx(financeMonths);
      }

      chips.forEach(b => b.addEventListener('click', () => setMonths(b.getAttribute('data-agpb-month'))));
      setMonths(financeMonths);
      _renderRepExFn = renderRepEx; // expose so renderFinance can call it on price changes
      const finModal        = root.querySelector('[data-agpb-fin-modal]');
      const finModalBdrop   = root.querySelector('[data-agpb-fin-modal-backdrop]');
      const finStep1        = finModal && finModal.querySelector('[data-agpb-fin-step="1"]');
      const finStep2        = finModal && finModal.querySelector('[data-agpb-fin-step="2"]');
      const finModalChips   = finModal && finModal.querySelector('[data-agpb-fin-modal-chips]');
      const finModalProvs   = finModal && finModal.querySelector('[data-agpb-fin-modal-providers]');
      const finModalPermo   = finModal && finModal.querySelector('[data-agpb-fin-modal-permo]');
      const finModalSuffix  = finModal && finModal.querySelector('.agpb__finModalFromSuffix');
      const finNextBtn      = finModal && finModal.querySelector('[data-agpb-fin-modal-next]');
      const finProceedBtn   = finModal && finModal.querySelector('[data-agpb-fin-modal-proceed]');
      const finProceedProv  = finModal && finModal.querySelector('[data-agpb-fin-proceed-provider]');
      const finBundleItems  = finModal && finModal.querySelector('[data-agpb-fin-bundle-items]');
      const finCartItems    = finModal && finModal.querySelector('[data-agpb-fin-cart-items]');
      const finBundleTotal  = finModal && finModal.querySelector('[data-agpb-fin-bundle-total]');
      const finCartTotal    = finModal && finModal.querySelector('[data-agpb-fin-cart-total]');

      let finModalTerm     = financeMonths; // inherits current active term
      let finModalProvider = null;          // { key, logo, checkoutUrl }

      // Only terms that have at least one provider configured
      function getTermsWithProviders() {
        return Object.keys(providersMap)
          .filter(t => Array.isArray(providersMap[t]) && providersMap[t].some(p => p && p.key))
          .map(Number)
          .sort((a, b) => a - b);
      }

      function openFinModal() {
        if (!finModal) return;
        finModalTerm     = financeMonths; // start from current active term
        finModalProvider = null;
        renderFinModalStep1();
        showFinStep(1);
        finModal.classList.remove('is-hidden');
        document.body.style.overflow = 'hidden';
      }

      function closeFinModal() {
        if (!finModal) return;
        finModal.classList.add('is-hidden');
        document.body.style.overflow = '';
      }

      function showFinStep(n) {
        if (finStep1) finStep1.style.display = n === 1 ? '' : 'none';
        if (finStep2) finStep2.style.display = n === 2 ? '' : 'none';
      }

      function renderFinModalStep1() {
        if (!finModal) return;
        const termsWithProviders = getTermsWithProviders();

        // Render term chips, only terms with providers
        if (finModalChips) {
          finModalChips.innerHTML = termsWithProviders.map(t => `
            <button type="button"
                    class="agpb__chip agpb__finModalChip${t === finModalTerm ? ' is-active' : ''}${t === 30 ? ' agpb__chip--bnpl' : ''}"
                    data-agpb-fin-modal-term="${t}">
              ${t === 30
                ? '<span class="agpb__chipLabel">Pay in 30</span><span class="agpb__chipSub">days</span>'
                : `<span class="agpb__chipLabel">${t}</span><span class="agpb__chipSub">months</span>`}
            </button>
          `).join('');

          finModalChips.querySelectorAll('[data-agpb-fin-modal-term]').forEach(btn => {
            btn.addEventListener('click', () => {
              finModalTerm     = Number(btn.getAttribute('data-agpb-fin-modal-term'));
              finModalProvider = null;
              if (finProvError) finProvError.classList.add('is-hidden');
              renderFinModalStep1();
            });
          });
        }

        // Per-month price vs Pay in 30 message
        const fromBox   = finModal.querySelector('[data-agpb-fin-modal-from-box]');
        const bnplMsg   = finModal.querySelector('[data-agpb-fin-modal-bnpl-msg]');
        const isBnpl    = finModalTerm === 30;
        if (fromBox)  fromBox.classList.toggle('is-hidden', isBnpl);
        if (bnplMsg)  bnplMsg.classList.toggle('is-hidden', !isBnpl);

        if (finModalPermo && !isBnpl) {
          const total = computeTotalCents();
          finModalPermo.textContent = formatMoney(finModalTerm > 0 ? Math.ceil(total / finModalTerm) : total);
          if (finModalSuffix) finModalSuffix.textContent = '/mo';
        }

        // Provider pills for selected term. Clickable to select
        if (finModalProvs) {
          const providers = (providersMap[String(finModalTerm)] || []).filter(p => p && p.key);
          finModalProvs.innerHTML = providers.map(p => {
            const label    = prettyKey(p.key);
            const logo     = p.logo ? `<img class="agpb__finLogo" src="${escHtml(p.logo)}" alt="${escHtml(label)}">` : '';
            const isActive = finModalProvider && finModalProvider.key === p.key;
            const url      = (checkoutUrls[String(finModalTerm)] || '').trim();
            return `<button type="button"
                            class="agpb__finModalProv${isActive ? ' is-active' : ''}"
                            data-fin-prov-key="${escHtml(p.key)}"
                            data-fin-prov-url="${escHtml(url)}">
                      ${logo}<span class="agpb__finProvText">${escHtml(label)}</span>
                    </button>`;
          }).join('');

          finModalProvs.querySelectorAll('[data-fin-prov-key]').forEach(btn => {
            btn.addEventListener('click', () => {
              const key = btn.getAttribute('data-fin-prov-key');
              const url = btn.getAttribute('data-fin-prov-url');
              finModalProvider = { key, checkoutUrl: url };
              finModalProvs.querySelectorAll('[data-fin-prov-key]').forEach(b => b.classList.remove('is-active'));
              btn.classList.add('is-active');
              if (finProvError) finProvError.classList.add('is-hidden');
            });
          });

          // Auto-select if only one provider for this term
          const allProvBtns = finModalProvs.querySelectorAll('[data-fin-prov-key]');
          if (allProvBtns.length === 1) {
            const btn = allProvBtns[0];
            finModalProvider = { key: btn.getAttribute('data-fin-prov-key'), checkoutUrl: btn.getAttribute('data-fin-prov-url') };
            btn.classList.add('is-active');
          }
        }
      }

      async function renderFinModalStep2() {
        if (!finModal) return;

        // Helper: render a thumbnail item card
        function finItemCard(img, title, price, qty) {
          const imgHtml = img
            ? `<img class="agpb__finItemThumb" src="${escHtml(img)}" alt="${escHtml(title)}">`
            : `<div class="agpb__finItemThumb agpb__finItemThumb--placeholder"></div>`;
          const qtyBadge = qty > 1 ? `<span class="agpb__finItemQty">×${qty}</span>` : '';
          return `<div class="agpb__finItem">
            ${imgHtml}
            <span class="agpb__finItemTitle">${escHtml(title)}${qtyBadge}</span>
            <span class="agpb__finItemPrice">${formatMoney(price * qty)}</span>
          </div>`;
        }

        // Bundle items (from builder selection)
        const bundleItems = [];
        if (baseVariantId) {
          const baseWrap = baseItems.find(w => Number(w.getAttribute('data-variant-id')) === baseVariantId);
          bundleItems.push({ title: baseTitle, price: basePrice, qty: Number(qty || 1), img: baseWrap ? baseWrap.getAttribute('data-follow-image') : '' });
        }
        getOrderedAddonSelections().forEach(({ sel }) => {
          if (sel && sel.variantId) bundleItems.push({ title: sel.title, price: sel.price, qty: Number(qty || 1), img: sel.followImage || '' });
        });

        const bundleTotalCents = bundleItems.reduce((s, i) => s + i.price * i.qty, 0);

        if (finBundleItems) {
          finBundleItems.innerHTML = bundleItems.map(i => finItemCard(i.img, i.title, i.price, i.qty)).join('');
        }
        if (finBundleTotal) finBundleTotal.textContent = formatMoney(bundleTotalCents);

        // Full cart. Fetch from Shopify
        if (finCartItems) finCartItems.innerHTML = `<div class="agpb__finCartLoading">Loading cart…</div>`;
        if (finCartTotal) finCartTotal.textContent = '';

        try {
          const cartRes  = await fetch('/cart.js', { signal: _timeoutSig(8000) });
          if (!cartRes.ok) throw new Error('cart read failed: ' + cartRes.status);
          const cartData = await cartRes.json();
          const cartItems = cartData.items || [];

          // Merge: existing cart + bundle items (de-dupe by variant ID, bundle takes precedence)
          const bundleIds = new Set([baseVariantId, ...getOrderedAddonSelections().map(({ sel }) => sel?.variantId)].filter(Boolean));
          const existingItems = cartItems.filter(ci => !bundleIds.has(ci.variant_id));
          const allItems = [
            ...bundleItems.map(i => ({ title: i.title, price: i.price, qty: i.qty, img: i.img })),
            ...existingItems.map(ci => ({
              title: ci.product_title + (ci.variant_title && ci.variant_title !== 'Default Title' ? `, ${ci.variant_title}` : ''),
              price: ci.price,
              qty  : ci.quantity,
              img  : ci.featured_image?.url || ''
            }))
          ];
          const fullTotalCents = allItems.reduce((s, i) => s + i.price * i.qty, 0);

          if (finCartItems) {
            finCartItems.innerHTML = allItems.map(i => finItemCard(i.img, i.title, i.price, i.qty)).join('');
          }
          if (finCartTotal) finCartTotal.textContent = formatMoney(fullTotalCents);
        } catch(e) {
          if (finCartItems) finCartItems.innerHTML = `<div class="agpb__finCartLoading">Could not load cart.</div>`;
        }

        // Update proceed button label
        if (finProceedProv && finModalProvider) finProceedProv.textContent = prettyKey(finModalProvider.key);
        const finNoteProvider = finModal.querySelector('[data-agpb-fin-note-provider]');
        if (finNoteProvider && finModalProvider) finNoteProvider.textContent = prettyKey(finModalProvider.key);
      }

      // Step navigation
      const finProvError = finModal && finModal.querySelector('[data-agpb-fin-prov-error]');

      if (finNextBtn) {
        finNextBtn.addEventListener('click', async () => {
          if (!finModalProvider) {
            // Show inline error and shake the providers area
            if (finProvError) {
              finProvError.classList.remove('is-hidden');
              finModalProvs && finModalProvs.classList.add('agpb__finModalProviders--shake');
              setTimeout(() => finModalProvs && finModalProvs.classList.remove('agpb__finModalProviders--shake'), 500);
            }
            return;
          }
          if (finProvError) finProvError.classList.add('is-hidden');
          await renderFinModalStep2();
          showFinStep(2);
        });
      }

      if (finModal) {
        finModal.querySelector('[data-agpb-fin-modal-back]')?.addEventListener('click', () => {
          showFinStep(1);
        });
      }

      // Close handlers
      [finModalBdrop, ...Array.from((finModal || document).querySelectorAll('[data-agpb-fin-modal-close]'))].forEach(el => {
        if (el) el.addEventListener('click', closeFinModal);
      });

      // Proceed. Add to cart and redirect
      if (finProceedBtn) {
        finProceedBtn.addEventListener('click', async () => {
          if (!baseVariantId || !finModalProvider) return;
          setError(null);

          // Same entry conditions as the main ATC/Buy-now path: a required
          // addon group must have a selection, and any unacknowledged
          // pre-order gate (e.g. a selection restored from a shared URL.
          // the exact case the ATC-time net exists for) must be accepted
          // before anything is added. The finance route used to skip both,
          // so incomplete bundles and unacknowledged pre-orders could reach
          // the finance checkout. Gate modals can't stack on the finance
          // modal, so it closes first; accepting every gate re-enters this
          // handler via the (hidden but still wired) proceed button.
          if (!validateRequired()) { closeFinModal(); return; }
          const pendingGates = collectPendingGates();
          if (pendingGates.length) {
            closeFinModal();
            runGateQueue(pendingGates, () => finProceedBtn.click());
            return;
          }

          const cartChoice  = finModal.querySelector('input[name="agpb-fin-cart"]:checked')?.value || 'bundle';
          const checkoutUrl = (finModalProvider.checkoutUrl || '').trim() || '/checkout';

          finProceedBtn.disabled = true;
          finProceedBtn.textContent = 'Adding to cart…';

          try {
            // Use the SAME builder as the main ATC so the finance route adds the REAL add-on variants
            // WITH the _bundle_id/_builder/_type/_group/_preorder props the discount function needs.
            // otherwise finance checkout silently loses every add-on bundle saving.
            const bundleLines = buildBundleItems();

            // The snapshot→clear→add sequence runs as ONE queued operation so a
            // concurrent writer (free-gift sync, validator) can never interleave
            // between the clear and the re-add.
            await _agQueue(async () => {
            // Every status below must be checked: fetch() only rejects on a network
            // failure, so a 422 (unpurchasable variant) or 429 (cart throttle) arrives
            // RESOLVED. Unchecked, we cleared the cart, silently failed to re-add, and
            // still redirected: sending the customer to finance checkout with an empty
            // cart and losing everything they had in it.
            let restoreLines = null;
            if (cartChoice === 'bundle') {
              // Snapshot what we are about to destroy so a failed re-add is recoverable.
              try {
                const snapRes = await fetch('/cart.js', { headers: { 'Accept': 'application/json' }, signal: _timeoutSig(8000) });
                if (snapRes.ok) {
                  const snap = await snapRes.json();
                  restoreLines = (snap.items || []).map((i) => ({
                    id: i.variant_id, quantity: i.quantity, properties: i.properties || {}
                  }));
                }
              } catch (e) { /* best-effort snapshot; the guards below still hold */ }

              const cleared = await fetch('/cart/clear.js', { method: 'POST', signal: _timeoutSig(8000) });
              if (!cleared.ok) throw new Error('cart clear failed: ' + cleared.status);
            }

            const added = await fetch('/cart/add.js', {
              method : 'POST',
              headers: { 'Content-Type': 'application/json' },
              body   : JSON.stringify({ items: bundleLines }),
              signal : _timeoutSig(8000)
            });

            if (!added.ok) {
              // Put the customer's cart back before surfacing the error, so a failed
              // finance attempt never costs them their basket.
              if (restoreLines && restoreLines.length) {
                try {
                  await fetch('/cart/add.js', {
                    method : 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body   : JSON.stringify({ items: restoreLines }),
                    signal : _timeoutSig(8000)
                  });
                } catch (e) { /* nothing more we can do; do NOT redirect */ }
              }
              let msg = 'Could not add to cart. Please try again.';
              try { const d = await added.json(); if (d && d.description) msg = d.description; } catch(e) {}
              throw new Error(msg);
            }
            });

            window.location.href = checkoutUrl;
          } catch(e) {
            console.error('[AGPB] Finance proceed error:', e);
            if (finProceedProv) finProceedProv.textContent = prettyKey(finModalProvider?.key || '');
            finProceedBtn.disabled = false;
            finProceedBtn.innerHTML = `Checkout with <span data-agpb-fin-proceed-provider>${escHtml(prettyKey(finModalProvider?.key || ''))}</span> →`;
          }
        });
      }
    }

    // ── Finance cards (BNPL + Payl8r: simplified two-card block) ────
    function fcCalcMonthlyCents(totalCents, term, monthlyRate) {
      if (term <= 0 || totalCents <= 0) return 0;
      if (!monthlyRate || monthlyRate <= 0) return Math.ceil(totalCents / term);
      const m = totalCents * monthlyRate / (1 - Math.pow(1 + monthlyRate, -term));
      return Math.ceil(m);
    }

    function renderFinanceCards(totalCents) {
      const fc = root.querySelector('[data-agpb-fc]');
      if (!fc) return;

      const total       = totalCents !== undefined ? totalCents : computeTotalCents();
      const minCents    = Number(fc.getAttribute('data-fc-min-cents')   || 0);
      const maxCents    = Number(fc.getAttribute('data-fc-max-cents')   || 0);
      const anchorTerm  = Number(fc.getAttribute('data-fc-anchor-term') || 12);
      const rateFromPct = parseFloat(fc.getAttribute('data-fc-monthly-rate-from') || '0');
      const aprRepPct   = parseFloat(fc.getAttribute('data-fc-apr-rep') || '0');
      const belowMinMsg = fc.getAttribute('data-fc-below-min-msg') || '';
      const belowMinHtml = fc.getAttribute('data-fc-below-min-html') || '';
      const aboveMaxMsg = fc.getAttribute('data-fc-above-max-msg') || '';

      const eligible   = fc.querySelector('[data-agpb-fc-eligible]');
      const outBox     = fc.querySelector('[data-agpb-fc-out]');
      const outText    = fc.querySelector('[data-agpb-fc-out-text]');
      const payl8rCard = fc.querySelector('[data-agpb-fc-payl8r]');

      // Threshold checks. Only on Payl8r card
      if (payl8rCard) {
        let outOfRange = false;
        if (minCents > 0 && total > 0 && total < minCents && belowMinMsg) {
          // Rendered by Liquid from merchant text via payl8r-link, so "Payl8r" links to the calculator.
          if (outText) {
            if (belowMinHtml) outText.innerHTML = belowMinHtml;
            else outText.textContent = belowMinMsg;
          }
          outOfRange = true;
        } else if (maxCents > 0 && total > maxCents && aboveMaxMsg) {
          if (outText) outText.textContent = aboveMaxMsg;
          outOfRange = true;
        }
        if (outOfRange) {
          if (eligible) eligible.classList.add('is-hidden');
          if (outBox)   outBox.classList.remove('is-hidden');
          return;
        }
        if (eligible) eligible.classList.remove('is-hidden');
        if (outBox)   outBox.classList.add('is-hidden');
      }

      // Build the term list (always-visible chips)
      const rawTerms = (fc.getAttribute('data-fc-other-terms') || '').replace(/\s+/g, '');
      let terms = rawTerms.split(',').map(t => parseInt(t, 10)).filter(t => t > 0);
      if (!terms.length) terms = [anchorTerm];
      if (!terms.includes(anchorTerm)) terms.unshift(anchorTerm);
      terms.sort((a, b) => a - b);

      // Active term. Initialized to anchor on first paint, persists through chip clicks
      let activeTerm = Number(fc.getAttribute('data-fc-active-term') || 0);
      if (!activeTerm || !terms.includes(activeTerm)) activeTerm = anchorTerm;
      fc.setAttribute('data-fc-active-term', String(activeTerm));

      const rateFromMonthly = rateFromPct / 100;
      const aprRepMonthly   = aprRepPct / 12 / 100;

      // Headline "From £X/mo". Uses the active term + marketing "from" rate
      const headlinePermo = fcCalcMonthlyCents(total, activeTerm, rateFromMonthly);
      const permoEl = fc.querySelector('[data-agpb-fc-permo-cents]');
      if (permoEl) permoEl.textContent = total > 0 ? formatMoney(headlinePermo) : formatMoney(0);

      // Render chips. Each chip shows term + per-month figure at "from" rate
      const chipsHost = fc.querySelector('[data-agpb-fc-chips]');
      if (chipsHost) {
        chipsHost.innerHTML = terms.map(t => {
          const m = fcCalcMonthlyCents(total, t, rateFromMonthly);
          const active = t === activeTerm;
          const priceHtml = total > 0
            ? formatMoney(m) + '<span class="agpb__fcChipMo">/mo</span>'
            : '-';
          return '<button type="button" class="agpb__fcChip' + (active ? ' is-active' : '') +
            '" data-agpb-fc-term="' + t + '" role="tab" aria-selected="' + (active ? 'true' : 'false') + '">' +
            '<span class="agpb__fcChipTerm">' + t + ' mo</span>' +
            '<span class="agpb__fcChipPermo">' + priceHtml + '</span>' +
            '</button>';
        }).join('');
      }

      // Representative example (uses rep APR + active term)
      const repPermo  = fcCalcMonthlyCents(total, activeTerm, aprRepMonthly);
      const repTotal  = repPermo * activeTerm;
      const repCharge = Math.max(0, repTotal - total);
      const repText   = fc.querySelector('[data-agpb-fc-rep-text]');
      if (repText) {
        if (total > 0) {
          repText.textContent =
            'Cash price ' + formatMoney(total) + '. No deposit. ' +
            activeTerm + ' monthly payments of ' + formatMoney(repPermo) + '. ' +
            'Total amount payable ' + formatMoney(repTotal) + '. ' +
            'Total charge for credit ' + formatMoney(repCharge) + '. ' +
            'Representative ' + aprRepPct + '% APR.';
        } else {
          repText.textContent = 'Select a product to see a representative example.';
        }
      }
    }

    function initFinanceCards() {
      const fc = root.querySelector('[data-agpb-fc]');
      if (!fc) return;

      // Seed active term to anchor
      const anchorTerm = fc.getAttribute('data-fc-anchor-term') || '12';
      fc.setAttribute('data-fc-active-term', anchorTerm);

      // Rep example tooltip
      const repBtn = fc.querySelector('[data-agpb-fc-rep-btn]');
      const repTip = fc.querySelector('[data-agpb-fc-rep-tip]');
      if (repBtn && repTip) {
        const toggleRep = (e) => {
          e.stopPropagation();
          const wasOpen = !repTip.classList.contains('is-hidden');
          repTip.classList.toggle('is-hidden', wasOpen);
          repBtn.setAttribute('aria-expanded', wasOpen ? 'false' : 'true');
        };
        repBtn.addEventListener('click', toggleRep);
        repBtn.addEventListener('touchend', (e) => { e.preventDefault(); toggleRep(e); });
        document.addEventListener('click', (e) => {
          if (!fc.contains(e.target)) {
            repTip.classList.add('is-hidden');
            repBtn.setAttribute('aria-expanded', 'false');
          }
        });
      }

      // Chip click. Change active term, re-render
      const chipsHost = fc.querySelector('[data-agpb-fc-chips]');
      if (chipsHost) {
        chipsHost.addEventListener('click', (e) => {
          const chip = e.target.closest('[data-agpb-fc-term]');
          if (!chip) return;
          fc.setAttribute('data-fc-active-term', chip.getAttribute('data-agpb-fc-term'));
          renderFinanceCards(computeTotalCents());
        });
      }

      // Initial paint
      renderFinanceCards(computeTotalCents());
    }

    // ── Init base selection ─────────────────────────────────────────
    function initBaseSelection() {
      if (!baseItems.length) return;
      // Every base option sold out: nothing can be bought, so the page must say
      // so instead of offering a live Add to cart that only errors.
      if (!baseItems.some(w => w.getAttribute('data-available') === 'true')) {
        enterSoldOutMode(baseItems[0]);
        return;
      }
      const pick = baseItems.find(w => w.getAttribute('data-available') === 'true') || baseItems[0];
      const btn  = pick.querySelector('.agpb__tile--base');
      if (!btn || btn.disabled) return;

      baseItems.forEach(w => setActive(w.querySelector('.agpb__tile--base'), false));
      setActive(btn, true);

      const d       = getWrapData(pick);
      baseVariantId = d.variantId;
      baseTitle     = d.title;
      basePrice     = d.price;

      renderBaseStatus(pick);
      baseStage = '';
      if (d.followImage) showBasePhoto(d, true);
    }

    // ── Sold-out mode (no buyable base option) ──────────────────────
    // Out of stock + Notify me under the selector, and both cart buttons
    // disabled and relabelled. The CSS hook .is-sold-out hides Buy now, the
    // dispatch timer, the total and the finance cards, which have nothing to price.
    function enterSoldOutMode(wrap) {
      root.classList.add('is-sold-out');
      renderBaseStatus(wrap);
      [root.querySelector('[data-agpb-atc-btn]'), root.querySelector('[data-agpb-sticky-btn]')].forEach(b => {
        if (!b) return;
        b.disabled = true;
        b.setAttribute('aria-disabled', 'true');
        const label = b.querySelector('.agpb__atcBtnLabel');
        if (label) label.textContent = 'Out of stock';
      });
    }

    // ── Validation ──────────────────────────────────────────────────
    function validateRequired() {
      if (!baseVariantId) {
        const baseTiles = root.querySelector('[data-agpb-base-tiles]');
        setError('Please select a base option.', baseTiles);
        return false;
      }
      /* A required group that is gated off is not applicable, so it must not block
         add-to-cart. Without this filter, configuring a required dependent group would
         make the builder permanently unsubmittable. */
      const reqGroups = Array.from(root.querySelectorAll('[data-agpb-group][data-required="true"]'))
        .filter(g => !g.hasAttribute('data-gated-off'));
      for (const g of reqGroups) {
        const key   = g.getAttribute('data-group-key');
        const multi = g.getAttribute('data-multi') === 'true';
        const hasSel = multi
          ? (addonMultiSelections.get(key) || []).length > 0
          : !!addonSelections.get(key);
        if (!hasSel) {
          const t = getTitleText(g.querySelector('.agpb__title')) || 'a required option';
          const grid = g.querySelector('[data-agpb-tiles]');
          setError(`Please choose an option in: ${t}`, grid);
          return false;
        }
      }
      return true;
    }

    // ── Build cart items ────────────────────────────────────────────
    function buildBundleItems() {
      const bundleId = `agpb-${Date.now().toString(36)}-${Math.random().toString(36).slice(2,7)}`;
      const items    = [];
      const baseProps = { _bundle_id: bundleId, _builder: 'true', _type: 'base' };

      // Source of truth: the CURRENT selected variant's data-status at the
      // moment cart-add fires. Previously we used the session "acceptPreorder"
      // flag which could go stale (in-stock variant getting flagged as
      // pre-order, or vice versa). Reading from the wrap is always accurate.
      const baseWrap = baseItems.find(w => Number(w.getAttribute('data-variant-id')) === baseVariantId);
      const baseIsPreorder = !!(baseWrap && baseWrap.getAttribute('data-status') === 'preorder');
      if (baseIsPreorder) {
        baseProps['_preorder'] = 'true';
        baseProps['Pre-order item'] = '⏳ This item is on pre-order and may take longer to dispatch.';
      }
      // Custom acknowledge status (e.g. End of Line): record the customer
      // agreed at purchase. Hidden marker + optional visible line note.
      if (baseWrap && baseWrap.getAttribute('data-status') === 'custom') {
        const cLabel = (baseWrap.getAttribute('data-custom-label') || '').trim() || 'End of Line';
        const cNote  = (baseWrap.getAttribute('data-custom-note')  || '').trim();
        baseProps['_custom_status'] = cLabel;
        if (cNote) baseProps[cLabel] = cNote;
      }
      // No-discount marker: the bundle discount function skips lines carrying this.
      if (baseWrap && baseWrap.getAttribute('data-no-discount') === 'true') {
        baseProps['_no_discount'] = 'true';
      }

      items.push({
        id         : Number(baseVariantId),
        quantity   : Number(qty || 1),
        properties : baseProps
      });

      getOrderedAddonSelections().forEach(({ sel, key, group }) => {
        if (!sel || !sel.available) return;
        const groupTitle = getTitleText(group?.querySelector('.agpb__title')) || key;
        const addonProps = { _bundle_id: bundleId, _builder: 'true', _type: 'addon', _group: groupTitle };
        // Same source-of-truth approach: trust the addon's actual status
        // captured into the selection object, not the modal-acceptance set.
        if ((sel.status || '') === 'preorder') {
          addonProps['_preorder'] = 'true';
          addonProps['Pre-order item'] = '⏳ This item is on pre-order and may take longer to dispatch.';
        }
        if ((sel.status || '') === 'custom') {
          const cLabel = (sel.customLabel || '').trim() || 'End of Line';
          const cNote  = (sel.customNote  || '').trim();
          addonProps['_custom_status'] = cLabel;
          if (cNote) addonProps[cLabel] = cNote;
        }
        if (sel.noDiscount) addonProps['_no_discount'] = 'true';
        items.push({
          id         : Number(sel.realVariant || sel.variantId), // Phase 4: add the REAL variant so the discount engine applies the bundle saving; falls back to the duplicate if no config match
          quantity   : Number(qty || 1),
          properties : addonProps
        });
      });

      return items;
    }

    // ── Cart fetch ──────────────────────────────────────────────────
    async function addToCart(items) {
      const res = await fetch('/cart/add.js', {
        method  : 'POST',
        headers : { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body    : JSON.stringify({ items }),
        signal  : _timeoutSig(8000)
      });
      if (!res.ok) {
        let msg = 'Could not add to cart. Please try again.';
        try { const d = await res.json(); if (d && d.description) msg = d.description; } catch(e) {}
        throw new Error(msg);
      }
      const result = await res.json();
      // Order-level flags for back of house. One cart attribute per warning
      // so they surface in the Shopify order's Additional details, same as
      // the long-standing pre-order flag.
      const attrs = {};
      const hasPreorder = items.some(i => i.properties && i.properties['_preorder'] === 'true');
      if (hasPreorder) {
        attrs['⚠️ Pre-order'] = 'This order contains one or more pre-order items. Please check line item details before dispatching.';
      }
      const customLabels = [...new Set(items.map(i => i.properties && i.properties['_custom_status']).filter(Boolean))];
      customLabels.forEach(label => {
        attrs['⚠️ ' + label] = 'This order contains one or more ' + label + ' items. Please check line item details before dispatching.';
      });
      if (Object.keys(attrs).length) {
        // House rule: an unchecked write is not a delivered write. One bounded
        // retry, then give up quietly. The line-item _preorder/_custom_status
        // props already landed with the add, only the order-level banner is at
        // stake, and the customer's add must never fail because of it.
        const postAttrs = () => fetch('/cart/update.js', {
          method  : 'POST',
          headers : { 'Content-Type': 'application/json' },
          body    : JSON.stringify({ attributes: attrs }),
          signal  : _timeoutSig(8000)
        });
        try {
          let attrRes = await postAttrs();
          if (!attrRes.ok) {
            await new Promise(r => setTimeout(r, 1200));
            attrRes = await postAttrs();
            if (!attrRes.ok) console.warn('[AGPB] order-attrs write failed twice:', attrRes.status);
          }
        } catch (e) { console.warn('[AGPB] order-attrs write error:', e); }
      }
      return result;
    }

    // ── ATC-time acknowledge net ────────────────────────────────────
    // Selections restored from the URL (or any other path that bypasses the
    // tile-click gate) never show the pre-order/custom acknowledge modal.
    // Before add-to-cart / buy-now we sweep the current selections for gated
    // statuses that haven't been acknowledged this session and run their
    // modals first; the purchase only proceeds once every gate is accepted.
    function collectPendingGates() {
      const pending = [];
      const gated = s => s === 'preorder';

      const baseWrap = baseItems.find(w => Number(w.getAttribute('data-variant-id')) === baseVariantId);
      if (baseWrap && gated((baseWrap.getAttribute('data-status') || '').trim()) && !acceptPreorder) {
        pending.push({
          wrap       : baseWrap,
          isAccepted : () => acceptPreorder,
          accept     : () => { acceptPreorder = true; }
        });
      }

      const pushAddon = (sel, key) => {
        if (!sel || !gated((sel.status || '').trim()) || addonPreorderAccepted.has(key)) return;
        const wrap = addonItems.find(w =>
          Number(w.getAttribute('data-variant-id')) === sel.variantId &&
          (w.getAttribute('data-group-key') || '') === key);
        if (!wrap) return;
        pending.push({
          wrap       : wrap,
          isAccepted : () => addonPreorderAccepted.has(key),
          accept     : () => addonPreorderAccepted.add(key)
        });
      };
      addonSelections.forEach((sel, key) => pushAddon(sel, key));
      addonMultiSelections.forEach((sels, key) => (sels || []).forEach(sel => pushAddon(sel, key)));

      return pending;
    }

    function runGateQueue(pending, onAllAccepted) {
      const next = () => {
        // Skip entries already acknowledged (e.g. an earlier accept in the
        // same addon group covered this one)
        while (pending.length && pending[0].isAccepted()) pending.shift();
        if (!pending.length) { onAllAccepted(); return; }
        const p = pending.shift();
        openStatusGate(
          getWrapData(p.wrap), p.wrap,
          () => { p.accept(); next(); },
          () => {} // cancel: abort the purchase, keep the selection as-is
        );
      };
      next();
    }

    // ── ATC button ──────────────────────────────────────────────────
    const atcBtn = root.querySelector('[data-agpb-atc-btn]');
    if (atcBtn) {
      atcBtn.addEventListener('click', async () => {
        setError(null);
        if (!validateRequired()) return;
        const pendingGates = collectPendingGates();
        if (pendingGates.length) { runGateQueue(pendingGates, () => atcBtn.click()); return; }
        if (_purchaseInFlight) return;
        _purchaseInFlight = true;
        const items = buildBundleItems();
        atcBtn.disabled = true;
        atcBtn.classList.add('is-loading');
        try {
          // Inner finally clears the flag as soon as the ADD completes, not after the
          // success animation below: the button stays disabled for that, but a genuine
          // second purchase must not be blocked for the animation's duration.
          try { await _agQueue(() => addToCart(items)); } finally { _purchaseInFlight = false; }
          atcBtn.classList.remove('is-loading');
          atcBtn.classList.add('is-success');
          const labelEl = atcBtn.querySelector('.agpb__atcBtnLabel');
          const origLabel = labelEl ? labelEl.textContent : '';
          if (labelEl) labelEl.textContent = 'Added to cart ✓';
          const qtyInputEl = root.querySelector('[data-agpb-qty]');
          if (qtyInputEl) { qtyInputEl.value = '1'; qty = 1; renderTotal(); }
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          document.dispatchEvent(new CustomEvent('on:cart:change'));
          /* Prefer the slide-out cart drawer when it exists on the page;
             only fall back to the /cart redirect if the drawer isn't
             rendered (e.g. cart_type != 'drawer' in theme settings).
             Refresh + open in one tick so the drawer animates in with
             the new line items already painted (no stale-content flash). */
          if (document.querySelector('cart-drawer')) {
            document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
            document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
          }
          else if (cfg.redirectToCart) { window.location.href = '/cart'; }
          setTimeout(() => {
            atcBtn.classList.remove('is-success');
            if (labelEl) labelEl.textContent = origLabel;
            atcBtn.disabled = false;
          }, 2000);
        } catch(err) {
          setError(err.message || 'Could not add to cart.');
          atcBtn.disabled = false;
          atcBtn.classList.remove('is-loading');
        }
      });
    }

    // ── Buy it now ──────────────────────────────────────────────────
    const buyNowBtn = root.querySelector('[data-agpb-buy-now]');
    if (buyNowBtn) {
      buyNowBtn.addEventListener('click', async () => {
        setError(null);
        if (!validateRequired()) return;
        const pendingGates = collectPendingGates();
        if (pendingGates.length) { runGateQueue(pendingGates, () => buyNowBtn.click()); return; }
        if (_purchaseInFlight) return;
        _purchaseInFlight = true;
        const items = buildBundleItems();
        buyNowBtn.disabled = true;
        buyNowBtn.classList.add('is-loading');
        try {
          try { await _agQueue(() => addToCart(items)); } finally { _purchaseInFlight = false; }
          window.location.href = '/checkout';
        } catch(err) {
          setError(err.message || 'Could not start checkout.');
        } finally {
          buyNowBtn.disabled = false;
          buyNowBtn.classList.remove('is-loading');
        }
      });
    }

    // ── Dispatch timer (below Buy it now) ───────────────────────────
    root.querySelectorAll('[data-agpb-dispatch]').forEach(function(el) {
      var textEl       = el.querySelector('[data-agpb-dispatch-text]');
      var cutHour      = parseInt(el.dataset.cutoffHour || '14', 10);
      var cutMin       = parseInt(el.dataset.cutoffMin  || '0',  10);
      var dispDays     = (el.dataset.cutoffDays || 'Mon,Tue,Wed,Thu,Fri').split(',').map(function(d){ return d.trim(); });
      var prefix       = el.dataset.prefix      || 'within';
      var suffix       = el.dataset.suffix      || 'for same-day dispatch';
      var afterPfx     = el.dataset.afterPrefix || 'before';
      var afterSfx     = el.dataset.afterSuffix || 'for dispatch on';
      var useBankHols  = el.dataset.bankHolidays === 'true';
      var dayNames     = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
      var dayLabels    = { Sun:'Sunday', Mon:'Monday', Tue:'Tuesday', Wed:'Wednesday', Thu:'Thursday', Fri:'Friday', Sat:'Saturday' };
      var bankHolSet   = {};

      function ymd(d) {
        return d.getFullYear() + '-' +
          (d.getMonth() < 9 ? '0' : '') + (d.getMonth() + 1) + '-' +
          (d.getDate() < 10 ? '0' : '') + d.getDate();
      }
      function isBankHol(d) { return useBankHols && !!bankHolSet[ymd(d)]; }
      function isDispatchDay(d) {
        var name = dayNames[d.getDay()];
        return dispDays.indexOf(name) > -1 && !isBankHol(d);
      }
      function nextDispatchDate(fromDate) {
        var d = new Date(fromDate);
        for (var i = 1; i <= 14; i++) {
          d.setDate(d.getDate() + 1);
          if (isDispatchDay(d)) return new Date(d);
        }
        return null;
      }
      function fmtCutoff() {
        var h12  = cutHour % 12 || 12;
        var ampm = cutHour >= 12 ? 'pm' : 'am';
        return h12 + (cutMin > 0 ? ':' + (cutMin < 10 ? '0' : '') + cutMin : '') + ampm;
      }

      var closureEnabled = el.dataset.closureEnabled === 'true';
      var closureStart   = el.dataset.closureStart || '';
      var closureEnd     = el.dataset.closureEnd   || '';
      var closureMsg     = el.dataset.closureMsg   || "We're closed. Check back soon.";

      function isInClosure() {
        if (!closureEnabled || !closureStart || !closureEnd) return false;
        var now = new Date();
        var s   = new Date(closureStart + 'T00:00:00');
        var e   = new Date(closureEnd   + 'T00:00:00');
        return now >= s && now < e;
      }

      function updateDispatch() {
        if (!textEl) return;
        if (isInClosure()) { textEl.innerHTML = closureMsg; return; }

        var now    = new Date();
        var cutoff = new Date(now);
        cutoff.setHours(cutHour, cutMin, 0, 0);

        var todayIsDispatch = isDispatchDay(now);
        var beforeCutoff    = todayIsDispatch && now < cutoff;

        if (beforeCutoff) {
          var diff = cutoff - now;
          var hh   = Math.floor(diff / 3600000);
          var mm   = Math.floor((diff % 3600000) / 60000);
          var ss   = Math.floor((diff % 60000) / 1000);
          var countdown = (hh > 0 ? hh + 'h ' : '') +
            (mm < 10 ? '0' : '') + mm + 'm' +
            (el.dataset.hideSeconds === 'true' ? '' : ' ' + (ss < 10 ? '0' : '') + ss + 's');
          textEl.innerHTML = prefix + ' <strong>' + countdown + '</strong> ' + suffix;
        } else {
          var nextDate  = nextDispatchDate(now);
          if (!nextDate) { textEl.innerHTML = afterPfx + ' <strong>' + fmtCutoff() + '</strong> ' + afterSfx; return; }
          var nextName  = dayNames[nextDate.getDay()];
          var nextLabel = dayLabels[nextName] || nextName;
          textEl.innerHTML = afterPfx + ' <strong>' + fmtCutoff() + ' ' + nextLabel + '</strong> ' + afterSfx + ' ' + nextLabel;
        }
      }

      if (useBankHols) {
        fetch('https://www.gov.uk/bank-holidays.json', { signal: _timeoutSig(8000) })
          .then(function(r) { return r.json(); })
          .then(function(data) {
            var events = (data['england-and-wales'] && data['england-and-wales'].events) || [];
            events.forEach(function(ev) { bankHolSet[ev.date] = true; });
          })
          .catch(function() {})
          .finally(function() { updateDispatch(); setInterval(updateDispatch, 1000); });
      } else {
        updateDispatch();
        setInterval(updateDispatch, 1000);
      }
    });

    // ── Sticky ATC → mirrors main ATC button ────────────────────────
    const stickyBtn = root.querySelector('[data-agpb-sticky-btn]');
    if (stickyBtn && atcBtn) stickyBtn.addEventListener('click', () => atcBtn.click());

    // ── Sticky ATC visibility (IntersectionObserver) ─────────────────
    const stickyBar       = root.querySelector('[data-agpb-sticky-atc]');
    const atcWrap         = root.querySelector('[data-agpb-atc]');
    const compositeCanvas = root.querySelector('[data-agpb-composite]');
    const viewBundleBtn   = root.querySelector('[data-agpb-view-bundle]');

    function setViewBundleVisible(show) {
      if (!viewBundleBtn) return;
      viewBundleBtn.classList.toggle('is-hidden', !show);
      requestAnimationFrame(() => viewBundleBtn.classList.toggle('is-ready', show));
    }

    if (viewBundleBtn) {
      viewBundleBtn.addEventListener('click', () => {
        renderCompositeCanvas();
        setViewBundleVisible(false);
      });
    }
    let   atcInView = true;
    function refreshStickyVisibility() {
      if (!stickyBar) return;
      stickyBar.classList.toggle('is-hidden', atcInView || !baseVariantId);
    }
    if (stickyBar && atcWrap && 'IntersectionObserver' in window) {
      const obs = new IntersectionObserver(entries => {
        atcInView = !!(entries[0] && entries[0].isIntersecting);
        refreshStickyVisibility();
      }, { threshold: 0.15 });
      obs.observe(atcWrap);
    }

    // ── Escape key closes any open modal ───────────────────────────
    document.addEventListener('keydown', e => {
      if (e.key !== 'Escape') return;
      if (poModal && !poModal.classList.contains('is-hidden')) {
        closePreorderModal();
        if (_poOnCancel) _poOnCancel();
        _poOnAccept = _poOnCancel = null;
      }
      if (nmModal && !nmModal.classList.contains('is-hidden')) closeNotifyModal();
    });

    // ── Bootstrap ───────────────────────────────────────────────────
    const selBlkInit = root.querySelector('[data-agpb-selection]');
    if (selBlkInit) selBlkInit.classList.add('is-hidden');
    const finBlkInit = root.querySelector('[data-agpb-finance]');
    if (finBlkInit) finBlkInit.classList.add('is-hidden');
    renderStaticMoney();
    // ── Learn more anchor links ──────────────────────────────────
    document.addEventListener('click', e => {
      const link = e.target.closest('.agpb__learnMore');
      if (!link) return;
      const href = link.getAttribute('href') || '';
      if (!href.startsWith('#')) return;
      e.preventDefault();
      const id = href.slice(1);

      // Find the addon-details section via desktop panel
      const panel = document.querySelector(`.aod-detail-panel[data-anchor="${id}"]`);
      if (!panel) return;
      const aodSection = panel.closest('section');

      // ── Desktop: activate panel + sidebar ───────────────────────
      aodSection.querySelectorAll('.aod-detail-panel').forEach(p => p.classList.remove('active'));
      aodSection.querySelectorAll('.aod-sidebar-item').forEach(b => { b.classList.remove('active'); b.setAttribute('aria-pressed', 'false'); });
      panel.classList.add('active');
      const sidebarBtn = aodSection.querySelector(`.aod-sidebar-item[data-target="${panel.id}"]`);
      if (sidebarBtn) { sidebarBtn.classList.add('active'); sidebarBtn.setAttribute('aria-pressed', 'true'); }

      // ── Mobile: find matching thumb card and click it ────────────
      const sheetPanel = aodSection.querySelector(`.aod-sheet-panel[data-anchor="${id}"]`);
      if (sheetPanel) {
        const blockId = sheetPanel.id.replace('sheet-panel-', '');
        const thumbCard = aodSection.querySelector(`.aod-thumb-card[data-target="mob-panel-${blockId}"]`);
        // Only trigger on mobile. Check if thumb cards are visible
        if (thumbCard && thumbCard.offsetParent !== null) thumbCard.click();
      }

      // Scroll to the section
      setTimeout(() => {
        const top = aodSection.getBoundingClientRect().top + window.scrollY - 120;
        window.scrollTo({ top, behavior: 'smooth' });
      }, 50);
    });

    initBaseSelection();
    restoreFromUrl();   // Must run before renderTotal so API call includes all pre-selected items
    /* After restore, so a crafted ?addon=<id> for a gated group cannot survive into
       the total or the cart when its dependency was never selected. */
    applyGroupGating();
    renderChips();
    renderTotal();
    // Self-heal any add-on tile whose baked-in price is stale vs the live config.
    (window.requestIdleCallback || function (fn) { return setTimeout(fn, 1200); })(function () { reconcileAddonPricesLive(); });
    updateBasePrices(); // Async. Fires standalone API call per base tile, independent of addon selection
    initFinance();
    initFinanceCards();
    // Defer sticky visibility so it doesn't cause layout shift on load
    requestAnimationFrame(refreshStickyVisibility);

    // ── Canvas composite ─────────────────────────────────────────────

    function getCompositeCells(count, size, gap) {
      const s = size, g = gap;
      if (count === 1) return [{ x:0, y:0, w:s, h:s }];
      if (count === 2) return [
        { x:0,         y:0, w:(s-g)/2, h:s },
        { x:(s+g)/2,   y:0, w:(s-g)/2, h:s }
      ];
      if (count === 3) return [
        { x:0,         y:0,       w:s,       h:(s-g)/2 },
        { x:0,         y:(s+g)/2, w:(s-g)/2, h:(s-g)/2 },
        { x:(s+g)/2,   y:(s+g)/2, w:(s-g)/2, h:(s-g)/2 }
      ];
      if (count === 4) return [
        { x:0,       y:0,       w:(s-g)/2, h:(s-g)/2 },
        { x:(s+g)/2, y:0,       w:(s-g)/2, h:(s-g)/2 },
        { x:0,       y:(s+g)/2, w:(s-g)/2, h:(s-g)/2 },
        { x:(s+g)/2, y:(s+g)/2, w:(s-g)/2, h:(s-g)/2 }
      ];
      if (count === 5) {
        const w3 = (s-2*g)/3, h = (s-g)/2;
        return [
          { x:0,        y:0,   w:w3, h },
          { x:w3+g,     y:0,   w:w3, h },
          { x:2*(w3+g), y:0,   w:w3, h },
          { x:0,        y:h+g, w:(s-g)/2, h },
          { x:(s+g)/2,  y:h+g, w:(s-g)/2, h }
        ];
      }
      // 6+
      const w = (s-2*g)/3, h = (s-g)/2;
      return [
        { x:0,        y:0,   w, h },
        { x:w+g,      y:0,   w, h },
        { x:2*(w+g),  y:0,   w, h },
        { x:0,        y:h+g, w, h },
        { x:w+g,      y:h+g, w, h },
        { x:2*(w+g),  y:h+g, w, h }
      ];
    }

    function roundedRect(ctx, x, y, w, h, r) {
      ctx.beginPath();
      ctx.moveTo(x+r, y);
      ctx.lineTo(x+w-r, y);
      ctx.quadraticCurveTo(x+w, y, x+w, y+r);
      ctx.lineTo(x+w, y+h-r);
      ctx.quadraticCurveTo(x+w, y+h, x+w-r, y+h);
      ctx.lineTo(x+r, y+h);
      ctx.quadraticCurveTo(x, y+h, x, y+h-r);
      ctx.lineTo(x, y+r);
      ctx.quadraticCurveTo(x, y, x+r, y);
      ctx.closePath();
    }

    function containFit(iw, ih, bw, bh) {
      const scale = Math.min(bw/iw, bh/ih);
      const w = iw*scale, h = ih*scale;
      return { x:(bw-w)/2, y:(bh-h)/2, w, h };
    }

    function drawComposite(ctx, size, imgs, labels, totalCount, maxCells, flags) {
      const gap    = Math.round(size * 0.018);
      const radius = Math.round(size * 0.045);
      const labelH = Math.round(size * 0.075);
      const count  = Math.min(imgs.length, maxCells);
      const imgPad = Math.round(size * 0.012);

      // Warm off-white background
      ctx.fillStyle = '#eeece8';
      ctx.fillRect(0, 0, size, size);

      // Helper: rounded rect with only top or bottom corners rounded
      function roundedRectTop(ctx, x, y, w, h, r) {
        ctx.beginPath();
        ctx.moveTo(x+r, y);
        ctx.lineTo(x+w-r, y);
        ctx.quadraticCurveTo(x+w, y, x+w, y+r);
        ctx.lineTo(x+w, y+h);
        ctx.lineTo(x, y+h);
        ctx.lineTo(x, y+r);
        ctx.quadraticCurveTo(x, y, x+r, y);
        ctx.closePath();
      }
      function roundedRectBottom(ctx, x, y, w, h, r) {
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x+w, y);
        ctx.lineTo(x+w, y+h-r);
        ctx.quadraticCurveTo(x+w, y+h, x+w-r, y+h);
        ctx.lineTo(x+r, y+h);
        ctx.quadraticCurveTo(x, y+h, x, y+h-r);
        ctx.lineTo(x, y);
        ctx.closePath();
      }

      function drawCell(img, label, x, y, w, h, isOverflow, overflowCount, isHero, flag) {
        const ih = h - labelH;

        // Shadow
        ctx.save();
        ctx.shadowColor = 'rgba(0,0,0,0.14)';
        ctx.shadowBlur  = 14;
        ctx.shadowOffsetY = 5;
        roundedRect(ctx, x, y, w, h, radius);
        ctx.fillStyle = '#fff';
        ctx.fill();
        ctx.restore();

        // Card bg
        roundedRect(ctx, x, y, w, h, radius);
        ctx.fillStyle = '#fff';
        ctx.fill();

        if (isOverflow) {
          roundedRect(ctx, x, y, w, h, radius);
          ctx.fillStyle = 'rgba(17,17,17,0.80)';
          ctx.fill();
          ctx.fillStyle = '#fff';
          ctx.font = '900 ' + Math.round(w * 0.22) + 'px -apple-system,sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('+' + overflowCount, x + w/2, y + h/2);
          return;
        }

        // Image area. Clip to top-rounded corners only
        ctx.save();
        roundedRectTop(ctx, x, y, w, ih, radius);
        ctx.clip();
        ctx.fillStyle = '#f4f4f4';
        ctx.fill();
        if (img && img.naturalWidth) {
          const fit = containFit(img.naturalWidth, img.naturalHeight, w - imgPad*2, ih - imgPad*2);
          ctx.drawImage(img, x + imgPad + fit.x, y + imgPad + fit.y, fit.w, fit.h);
        }
        // Subtle gradient fade at bottom of image into label
        const fadeGrad = ctx.createLinearGradient(0, y + ih - labelH * 0.6, 0, y + ih);
        fadeGrad.addColorStop(0, 'rgba(255,255,255,0)');
        fadeGrad.addColorStop(1, 'rgba(255,255,255,0.55)');
        ctx.fillStyle = fadeGrad;
        ctx.fillRect(x, y + ih - labelH * 0.6, w, labelH * 0.6);
        ctx.restore();

        // Thin separator line
        ctx.beginPath();
        ctx.moveTo(x + radius, y + ih);
        ctx.lineTo(x + w - radius, y + ih);
        ctx.strokeStyle = 'rgba(0,0,0,0.06)';
        ctx.lineWidth = 1;
        ctx.stroke();

        // Label bar. Clip to bottom-rounded corners only
        ctx.save();
        roundedRectBottom(ctx, x, y + ih, w, labelH, radius);
        ctx.clip();
        ctx.fillStyle = '#fff';
        ctx.fill();
        ctx.restore();

        // Label text
        ctx.fillStyle = '#222';
        const fontSize = Math.max(9, Math.min(14, Math.round(w / 9)));
        ctx.font = '700 ' + fontSize + 'px -apple-system,sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        let text = label;
        const maxW = w - gap * 3;
        while (text.length > 3 && ctx.measureText(text).width > maxW) {
          text = text.slice(0, -4) + '…';
        }
        ctx.fillText(text, x + w/2, y + ih + labelH/2);

        // Gated-status overlay. Coloured 2px outline around the cell + a
        // small pill at the top (red PRE-ORDER, amber custom label) so the
        // customer sees the flagged item at a glance without having to look
        // at the tiles. Drawn after the label so it sits on top.
        if (flag) {
          ctx.save();
          roundedRect(ctx, x + 1, y + 1, w - 2, h - 2, Math.max(1, radius - 1));
          ctx.strokeStyle = flag.color;
          ctx.lineWidth   = 2;
          ctx.stroke();
          // Pill
          var pillFs = Math.max(8, Math.round(w * 0.085));
          var pillPx = Math.round(pillFs * 0.55);
          var pillPy = Math.round(pillFs * 0.4);
          ctx.font = '900 ' + pillFs + 'px -apple-system, sans-serif';
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          var pillText = String(flag.text || '').toUpperCase();
          var pillTw = ctx.measureText(pillText).width;
          var pillW = pillTw + pillPx * 2;
          var pillH = pillFs + pillPy * 2;
          var pillX = x + Math.round(w * 0.04);
          var pillY = y + Math.round(w * 0.04);
          roundedRect(ctx, pillX, pillY, pillW, pillH, Math.round(pillH / 2));
          ctx.fillStyle = flag.color;
          ctx.fill();
          ctx.fillStyle = '#fff';
          ctx.fillText(pillText, pillX + pillPx, pillY + pillH / 2 + 1);
          ctx.restore();
        }
      }

      // ── Hero + supporting layout ──────────────────────────────────
      const addons = count - 1;

      if (addons === 0) {
        drawCell(imgs[0], labels[0], gap, gap, size - gap*2, size - gap*2, false, 0, true, (flags && flags[0]) || null);
        return;
      }

      // Layout geometry
      const heroW    = Math.round(size * 0.60) - gap;
      const heroX    = gap;
      const heroY    = gap;
      const heroH    = size - gap * 2;

      const supX     = heroX + heroW + gap;
      const supW     = size - supX - gap;
      const supCount = Math.min(addons, maxCells - 1);

      // "Bundled Products" pill above right column
      const pillH    = Math.round(size * 0.06);
      const pillY    = gap;
      const pillFS   = Math.max(8, Math.round(size * 0.028));
      ctx.save();
      roundedRect(ctx, supX, pillY, supW, pillH, Math.round(pillH / 2));
      ctx.fillStyle = 'rgba(30,30,30,0.55)';
      ctx.fill();
      ctx.fillStyle = '#fff';
      ctx.font = '600 ' + pillFS + 'px -apple-system,sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('Bundled Products', supX + supW/2, pillY + pillH/2);
      ctx.restore();

      const cardsY   = pillY + pillH + gap;
      const cardsH   = size - cardsY - gap;
      const supH     = (cardsH - (supCount - 1) * gap) / supCount;

      drawCell(imgs[0], labels[0], heroX, heroY, heroW, heroH, false, 0, true, (flags && flags[0]) || null);

      for (let i = 0; i < supCount; i++) {
        const sy     = cardsY + i * (supH + gap);
        const isOver = i === supCount - 1 && totalCount > maxCells;
        drawCell(
          imgs[i + 1] || null,
          labels[i + 1] || '',
          supX, sy, supW, supH,
          isOver, totalCount - maxCells + 1,
          false,
          (flags && flags[i + 1]) || null
        );
      }
    }


    function renderCompositeCanvas() {
      if (!compositeCanvas) return;

      // Collect items
      const items = [];
      if (baseVariantId) {
        const baseWrap = baseItems.find(w => Number(w.getAttribute('data-variant-id')) === baseVariantId);
        const img = baseWrap ? baseWrap.getAttribute('data-follow-image') : '';
        const status = baseWrap ? (baseWrap.getAttribute('data-status') || '') : '';
        // Pass the block's custom colour like every other statusFlag call site:
        // without it the canvas ribbon falls back to the default amber while the
        // tiles and notice honour the merchant's pick.
        // A bundle base tile lists its component images ("url|title" joined by "||"):
        // show each one in the collage instead of the single product image.
        const bundleImgs = baseWrap ? (baseWrap.getAttribute('data-bundle-images') || '') : '';
        if (bundleImgs) {
          bundleImgs.split('||').forEach(part => {
            const cut = part.indexOf('|');
            const bImg = cut > 0 ? part.slice(0, cut) : part;
            if (bImg) items.push({ img: bImg, title: cut > 0 ? part.slice(cut + 1) : baseTitle, flag: null });
          });
        } else if (img) items.push({ img, title: baseTitle, flag: statusFlag(status, baseWrap ? baseWrap.getAttribute('data-custom-label') : '', baseWrap ? baseWrap.getAttribute('data-custom-color') : '') });
      }
      getOrderedAddonSelections().forEach(({ sel }) => {
        if (sel && sel.followImage) items.push({ img: sel.followImage, title: sel.title, flag: statusFlag(sel.status || '', sel.customLabel, sel.customColor) });
      });

      // Need 2+ items to show composite
      if (items.length < 2) {
        compositeCanvas.classList.remove('is-visible');
        // Restore base product image in the stage
        if (mediaStage) {
          mediaStage.style.transition = 'opacity .3s ease';
          // If we have a base selection with a follow image, restore it
          if (baseVariantId) {
            const baseWrap = baseItems.find(w => Number(w.getAttribute('data-variant-id')) === baseVariantId);
            const baseImg  = baseStage || (baseWrap ? stagePhotoFor(baseWrap.getAttribute('data-follow-image')) : '');
            if (baseImg && stageShows(baseImg)) {
              mediaStage.dataset.agpbWant = baseImg;
            } else if (baseImg) {
              const newImg     = new Image();
              // CLS FIX 2026-08-17: same reason as swapToUrl, and worse here,
              // because the onload below clears the stage (innerHTML = '')
              // before appending. Without intrinsic dimensions the stage
              // collapses to 0 in that gap.
              newImg.width     = 1400;
              newImg.height    = 1400;
              newImg.className = 'agpb__mainImg';
              newImg.alt       = baseTitle || '';
              // Same stale-onload guard as swapToUrl: if the user picked
              // something else (e.g. an in-action clip) after this restore
              // began, do not clear the stage under them.
              mediaStage.dataset.agpbWant = baseImg;
              newImg.src       = baseImg;
              newImg.onload    = () => {
                if (mediaStage.dataset.agpbWant !== baseImg) return;
                mediaStage.innerHTML = '';
                mediaStage.appendChild(newImg);
              };
            }
          }
          mediaStage.style.opacity = '1';
        }
        setTimeout(() => {
          compositeCanvas.classList.add('is-hidden');
          if (mediaStage) mediaStage.style.transition = '';
        }, 320);
        return;
      }

      const token = ++compositeRenderToken;
      const maxCells = Math.min(items.length, 6);
      const container = compositeCanvas.parentElement;
      const size = container.offsetWidth || 500;
      const dpr  = window.devicePixelRatio || 1;

      compositeCanvas.width  = size * dpr;
      compositeCanvas.height = size * dpr;
      compositeCanvas.style.width  = size + 'px';
      compositeCanvas.style.height = size + 'px';

      const ctx = compositeCanvas.getContext('2d');
      ctx.scale(dpr, dpr);

      // Load images
      let loaded = 0;
      const imgEls = new Array(maxCells).fill(null);

      items.slice(0, maxCells).forEach((item, i) => {
        const el = new Image();
        el.crossOrigin = 'anonymous';
        const done = () => {
          if (token !== compositeRenderToken) return;
          imgEls[i] = el.naturalWidth ? el : null;
          if (++loaded === maxCells) {
            drawComposite(ctx, size, imgEls, items.map(it => it.title), items.length, maxCells, items.map(it => it.flag || null));
            compositeCanvas.classList.remove('is-hidden');
            requestAnimationFrame(() => compositeCanvas.classList.add('is-visible'));
            setViewBundleVisible(false);
            if (mediaStage) {
              mediaStage.style.transition = 'opacity .3s ease';
              mediaStage.style.opacity = '0';
            }
          }
        };
        el.onload  = done;
        el.onerror = done;
        // Append version param to bust CORS cache issues
        el.src = item.img + (item.img.includes('?') ? '&' : '?') + 'v=cors';
      });
    }

    // Hook canvas click to open lightbox
    if (compositeCanvas) {
      compositeCanvas.addEventListener('click', () => openLightbox(activeIndex));
    }

    // ── Restore selections from URL params ──────────────────────────
    // Called during init (before renderTotal) so the first API call
    // includes all pre-selected items in a single request.
    function restoreFromUrl() {
      const params = new URLSearchParams(window.location.search);
      const variantParam = params.get('variant');
      const addonParams  = params.getAll('addon');

      // Restore base variant. Call commitBaseSelection but suppress its
      // renderTotal; init sequence will fire one renderTotal afterwards.
      if (variantParam) {
        const baseWrap = baseItems.find(w => String(w.getAttribute('data-variant-id')) === String(variantParam));
        if (baseWrap) {
          const btn = baseWrap.querySelector('.agpb__tile--base');
          if (btn && !btn.disabled) commitBaseSelection(baseWrap, btn, { silent: true });
        }
      }

      // Restore addon selections silently. Same reason
      if (addonParams.length) {
        addonParams.forEach(addonId => {
          const addonWrap = addonItems.find(w => String(w.getAttribute('data-variant-id')) === String(addonId));
          if (addonWrap) {
            const btn = addonWrap.querySelector('.agpb__tile--addon');
            if (btn && !btn.disabled) commitAddonSelection(addonWrap, btn, { silent: true });
          }
        });
      }
    }

    // Re-render composite once layout is stable (handles URL param restore)
    window.addEventListener('load', () => setTimeout(renderCompositeCanvas, 100));

    // ── Shopify preview bar offset ───────────────────────────────────
    function checkPreviewBar() {
      const bar = document.getElementById('preview-bar-iframe');
      if (bar && stickyBar) {
        stickyBar.style.setProperty('--agpb-preview-bar', (bar.offsetHeight || 56) + 'px');
        stickyBar.classList.add('has-preview-bar');
      }
    }
    checkPreviewBar();
    window.addEventListener('load', checkPreviewBar);

    // Signal layout-stable to the CSS so the min-height fallback (set
    // to prevent CLS while we hydrate) can release. By this point
    // variant selection, price, addons, finance card etc. are rendered
    // at their final dimensions. CSS rule:
    //   .agpb__wrap[data-agpb-ready] { min-height: 0; }
    requestAnimationFrame(() => {
      const wrap = root.querySelector('.agpb__wrap');
      if (wrap) wrap.setAttribute('data-agpb-ready', '1');
    });

  }

  function bootAll(scope) {
    (scope || document).querySelectorAll('.agpb[data-agpb-section-id]').forEach(function (root) {
      var island = root.querySelector('script[type="application/json"][data-agpb-config]');
      if (!island) return;
      var cfg;
      try { cfg = JSON.parse(island.textContent); } catch (e) { return; }
      initSection(root, cfg);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { bootAll(); });
  } else {
    bootAll();
  }
  document.addEventListener('shopify:section:load', function (e) { bootAll(e.target); });

  window.AGPB = { initSection: initSection, bootAll: bootAll };
})();
